<template>
    <div
        :class="[
            'bo-table-div table-v2-new',
            {
                'is-collapsible': hasSubrecord,
            },
        ]"
    >
        <!-- Title & Column Sorting -->
        <slot
            name="header"
            :title="title"
            :compact="compactView"
            :expanded="visible"
            :displayHeaderFilters="displayHeaderFilters"
            :allHeadersSelected="allHeadersSelected"
            :columns="columns"
            :toggleVisibility="changeTableVisibility"
            :toggleCompact="changeCompactView"
            :toggleHeaderFiltersVisibility="toggleHeaderFiltersVisibility"
            :hideHeaderFilters="hideHeaderFilters"
            :setAllHeaders="setAllHeaders"
            :resetHeadersToDefault="resetHeadersToDefault"
            :changeTitle="changeTitle"
            :changeColumns="changeColumns"
        >
            <div
                class="table-header bo-table-header"
                v-if="displayTitleHeader"
                :class="{ 'hide-header': !showHeader, 'form-table-header': isForm }"
            >
                <div class="left-panel">
                    <div class="left-panel-wrapper">
                        <!-- leading slot -->
                        <slot name="leading-slot"></slot>

                        <!-- collapse the whole table -->
                        <div
                            v-if="isCollapsible"
                            class="indicator cursor-pointer"
                            :class="{ collapsed: !visible }"
                            @click="changeTableVisibility"
                            data-toggle="collapse"
                            :data-target="'#' + tableId + '_table'"
                        >
                            <i class="icon icon-expandmore"></i>
                        </div>

                        <!-- title & option to show required -->
                        <div class="d-flex align-items-center">
                            <span
                                class="title"
                                :class="{ required: requiredTable }"
                                :id="tableId + '_label'"
                            >
                                {{ tableTitle }}
                            </span>
                            <!-- <i
                                v-if="selectedRecordsCount"
                                class="icon-close cursor-pointer"
                                style="display: inline-block"
                                @click="clearSelection"
                            ></i> -->
                        </div>

                        <div class="action-panel">
                            <template v-for="menu in headerLeftMenu">
                                <a
                                    v-if="'redirect' == menu.type"
                                    :href="menu.url"
                                    class="editBtn"
                                    :class="menu.additional_classes"
                                >
                                    <i :class="menu.icon ? menu.icon : 'icon-edit'"></i>
                                    <span>{{ menu.label }}</span>
                                </a>

                                <div
                                    v-else-if="'select' == menu.type"
                                    class="status"
                                >
                                    <span>{{ menu.name }}</span>
                                    <bo-select
                                        v-model="menu.value"
                                        :readonly="!(menu.editable ?? true)"
                                        :options="menu.options"
                                        :labels="menu.labels"
                                        input-style="status-bordered cursor-pointer"
                                    >
                                    </bo-select>
                                </div>

                                <slot
                                    v-else-if="'manual_trigger' == menu.type"
                                    name="header_action_menu"
                                    :menu="menu"
                                ></slot>
                            </template>
                        </div>

                        <slot
                            name="left_header_buttons"
                            :selectedRecords="selectedRecords"
                        ></slot>
                        <small
                            v-if="requiredMessage"
                            class="text-danger pl-2 form-error"
                        >
                            <i class="icon-cancelcircle-1"></i>
                            {{ requiredMessage }}
                        </small>
                    </div>
                </div>

                <div class="right-panel">
                    <template v-for="menu in headerRightMenu">
                        <button
                            v-if="'download' == menu.type"
                            class="btn arclight importBtn"
                            @click="downloadRecords(menu.url, menu.file_name)"
                        >
                            <i class="icon-clouddownload"></i>
                            <span>{{ menu.label }}</span>
                        </button>
                        <template v-else-if="'upload' == menu.type">
                            <button
                                @click="triggerFileInput(menu.id)"
                                class="btn arclight importBtn"
                            >
                                <i class="icon-getapp"></i>
                                <span>{{ menu.label }}</span>
                            </button>
                            <input
                                class="hide"
                                :id="menu.id + '-file-import'"
                                :name="menu.id + '-file-import'"
                                type="file"
                                accept=".xlsx"
                                @change="importRecords($event, menu.url)"
                            />
                        </template>
                    </template>

                    <slot
                        name="right_header_buttons"
                        :selectedRecords="selectedRecords"
                        :search="search"
                    ></slot>

                    <div
                        class="btn transparent arclight"
                        :hidden="hideRefreshButton"
                        @click="refreshRecords()"
                    >
                        <i class="icon-refresh"></i>
                        <span>{{ generalTranslations.refresh ?? "Refresh" }}</span>
                    </div>

                    <div
                        class="btn btn-rounded arclight light"
                        :hidden="!draggable || hideSequenceSaveButton || localSearchSort"
                        @click="saveRecordsSequence()"
                    >
                        <span>{{ generalTranslations.save ?? "Save" }}</span>
                    </div>

                    <slot name="trailing-slot"></slot>
                </div>
            </div>

            <slot name="announcement-bar"></slot>

            <slot name="error-status-frame"></slot>

            <template v-if="showSortFilterStatusBar">
                <div
                    class="sort-filter-status-bar d-flex align-items-center"
                    v-if="draggable && (sortColumn || Object.values(search).length)"
                >
                    <template v-if="sortColumn">
                        <span class="sort-filter-status-label"
                            >{{ generalTranslations.sorted_by ?? "Sorted By" }}:</span
                        >
                        <span class="badge light-gray dark-text sort-filter-status-tag">
                            {{ sortedColumnLabel }}
                            <i
                                class="icon-cancelcircle-1 cursor-pointer"
                                @click="clearSort"
                            ></i>
                        </span>
                    </template>
                    <template v-if="Object.values(search).length">
                        <span class="sort-filter-status-label"
                            >{{ generalTranslations.filtered_by ?? "Filtered By" }}:</span
                        >
                        <span
                            v-for="filter in activeFilters()"
                            :key="filter.columnId"
                            class="badge light-gray dark-text sort-filter-status-tag"
                        >
                            {{ filter.label }}: {{ filter.value }}
                            <i
                                class="icon-cancelcircle-1 cursor-pointer"
                                @click="clearFilter(filter.columnId)"
                            ></i>
                        </span>
                    </template>
                    <a
                        class="clear-all-link cursor-pointer"
                        @click="clearAllFilters"
                    >
                        {{ generalTranslations.clear_all ?? "Clear All" }}
                    </a>
                    <div class="apply-as-sequence-wrapper">
                        <a
                            class="apply-as-sequence-link"
                            :class="{ disabled: !localDraggable }"
                            @click="localDraggable && applyCurrentOrderAsSequence()"
                        >
                            {{ generalTranslations.apply_as_sequence ?? "Apply As Sequence" }}
                        </a>
                        <i
                            v-if="!localDraggable"
                            class="icon-helpcircle"
                            data-toggle="tooltip"
                            data-placement="bottom"
                            :title="generalTranslations.remove_filters_to_enable_sequence ?? 'Remove filters to enable'"
                        ></i>
                    </div>
                </div>
            </template>

            <div
                class="function-panel"
                :class="{ 'collapsed-panel': !visible }"
            >
                <div
                    class="selected-panel"
                    v-if="enableViewSelectedRecords"
                >
                    <div
                        v-if="selectedRecordsCount > 0"
                        class="d-flex align-items-center selected-count"
                    >
                        {{ generalTranslations.selected ?? "Selected" }} ({{
                            selectedAllRecords ? totalAllRecords : selectedRecordsCount
                        }})
                        <i
                            class="icon-close cursor-pointer"
                            @click="clearSelection"
                        ></i>
                    </div>
                    <div class="d-flex align-items-center selected-action">
                        <div
                            v-if="
                                editable && headerChecked && showSelectAll && totalAllRecords > 0 && !selectedAllRecords
                            "
                            id="select_all_button"
                            class="text-arclight d-flex align-items-center selected-action-item cursor-pointer"
                            @click="selectAll"
                        >
                            {{ generalTranslations.select_all ?? "Select All" }} ({{ totalAllRecords }})
                        </div>
                        <span
                            v-if="editable && showSelectAll && totalAllRecords > 0 && !selectedAllRecords"
                            class="divider"
                        ></span>
                        <div
                            v-if="editable && deleteMultiple"
                            id="multiple_delete_button"
                            class="d-flex align-items-center selected-action-item"
                            :class="
                                selectedRecordsCount && isSelectedRecordsDeletable
                                    ? 'alert-color cursor-pointer'
                                    : 'disabled'
                            "
                            @click="selectedRecordsCount && isSelectedRecordsDeletable ? deleteSelectedRecords() : null"
                        >
                            <i class="icon-deleteforever"></i>
                            {{ generalTranslations.delete ?? "Delete"
                            }}<span v-if="selectedRecordsCount > 0"> ({{ selectedRecordsCount }})</span>
                        </div>
                        <slot
                            name="selected_panel_header"
                            :selectedRecords="selectedRecords"
                            :selectedRecordsCount="selectedRecordsCount"
                        ></slot>
                    </div>
                </div>

                <div class="filter-listview-panel">
                    <slot name="left_header"></slot>

                    <div
                        class="column-filter"
                        :class="{ 'is-column-filter-open': displayHeaderFilters }"
                        :hidden="hideDisplayHeaderFilter"
                    >
                        <button
                            id="filters_visibility_button"
                            type="button"
                            class="btn-rounded arclight bordered dropdown-toggle"
                            aria-haspopup="true"
                            aria-expanded="false"
                            @click="toggleHeaderFiltersVisibility"
                        >
                            <i class="icon-columnlist"></i>
                        </button>

                        <!-- COLUMN SORTING -->
                        <div
                            id="column_filter_dropdown_menu_right"
                            class="dropdown-menu dropdown-menu-right"
                            style="display: block"
                            v-if="displayHeaderFilters"
                            v-click-outside="hideHeaderFilters"
                        >
                            <div class="column-sorting">
                                <div class="column-header column">
                                    <input
                                        id="column_header_column_filter_checkbox"
                                        type="checkbox"
                                        v-model="allHeadersSelected"
                                        :indeterminate.prop="isIndeterminate"
                                        @change="setAllHeaders"
                                    />
                                    <span id="column_header_column_filter_label">
                                        {{ generalTranslations.column_headers ?? "Column Headers" }}
                                    </span>
                                </div>
                                <draggable
                                    v-model="columnSequence"
                                    v-bind="dragOptions"
                                    handle=".icon-dragindicator"
                                    @start="drag = true"
                                    @end="drag = false"
                                >
                                    <transition-group
                                        type="transition"
                                        :name="!drag ? 'flip-list' : null"
                                    >
                                        <slot
                                            name="column_sorting_sequence"
                                            :columnSequence="columnSequence"
                                        >
                                            <template v-for="header in columnSequence">
                                                <div
                                                    class="column"
                                                    :key="header.id"
                                                    v-if="!header.disabled"
                                                >
                                                    <template>
                                                        <i class="icon-dragindicator"></i>
                                                        <input
                                                            :id="header.id + '_column_filter_checkbox'"
                                                            v-model="header.visible"
                                                            type="checkbox"
                                                            :value="header"
                                                            @change="saveColumnPreference(columnSequence)"
                                                        />
                                                        <span
                                                            :id="header.id + '_column_filter_label'"
                                                            class="column-span-text"
                                                        >
                                                            {{ header.label }}</span
                                                        >
                                                    </template>
                                                </div>
                                            </template>
                                        </slot>
                                    </transition-group>
                                </draggable>
                            </div>

                            <!-- temp disabled, cannot work -->
                            <div class="column-filter-footer cursor-pointer">
                                <div
                                    id="column_filter_reset_button"
                                    class="reset-text"
                                    @click="resetHeadersToDefault"
                                >
                                    <i class="icon icon-cached"></i>
                                    {{ generalTranslations.reset ?? "Reset" }}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div
                        class="toggle list-view-mode"
                        :hidden="hideDisplayHeaderFilter"
                    >
                        <label class="switch">
                            <input
                                type="checkbox"
                                class="toggle-compact"
                                @change="changeCompactView"
                                checked
                            />
                            <span class="slider round"></span>
                            <div class="icon-density">
                                <i class="icon-densitydefault"></i>
                                <i class="icon-densitycompact"></i>
                            </div>
                        </label>
                    </div>
                </div>
            </div>
            <!-- <div
                class="selected-panel"
                v-if="selectedRecordsCount && enableViewSelectedRecords"
            >
                <div class="d-flex align-items-center selected-count">
                    {{ generalTranslations.selected ?? "Selected" }} ({{
                        selectedAllRecords ? totalAllRecords : selectedRecordsCount
                    }})
                    <i
                        class="icon-close cursor-pointer"
                        @click="clearSelection"
                    ></i>
                </div>
                <div class="d-flex align-items-center selected-action">
                    <div
                        v-if="editable && headerChecked && showSelectAll && totalAllRecords > 0 && !selectedAllRecords"
                        id="select_all_button"
                        class="text-arclight d-flex align-items-center selected-action-item cursor-pointer"
                        @click="selectAll"
                    >
                        {{ generalTranslations.select_all ?? "Select All" }} ({{ totalAllRecords }})
                    </div>
                    <span class="divider"></span>
                    <div
                        v-if="editable && deleteMultiple && selectedRecordsCount && isSelectedRecordsDeletable"
                        id="multiple_delete_button"
                        class="d-flex align-items-center selected-action-item alert-color cursor-pointer"
                        @click="deleteSelectedRecords"
                    >
                        <i class="icon-deleteforever"></i>
                        {{ generalTranslations.delete ?? "Delete" }} ({{ selectedRecordsCount }})
                    </div>
                    <slot
                        name="selected_panel_header"
                        :selectedRecords="selectedRecords"
                    ></slot>
                </div>
            </div> -->
        </slot>

        <data-table
            :class="[
                {
                    'bo-table': true,
                    'is-compact': localCompactView,
                    // 'no-title': !displayTitleHeader,
                    'popup-modal-no-title': !displayTitleHeader,
                    'form-table-body': isForm,
                    'has-data': Object.values(recordData).length > 0,
                    'has-no-data': Object.values(recordData).length <= 0 && !isOverview,
                    'is-overview': isOverview,
                    'selected-count': selectedRecordsCount > 0 && enableViewSelectedRecords,
                },
                tableStyle,
            ]"
            class="collapse show"
            :style="overviewTableStyle()"
            :columns="[]"
            :classes="tableClasses"
            :perPage="perPage"
            :ref="'datatable_' + tableId"
            :id="isCollapsible ? tableId + '_table' : ''"
        >
            <div slot="filters"></div>

            <!-- Datatable Header -->
            <thead slot="header">
                <!-- search header -->
                <tr
                    class="enable-search"
                    v-if="enableSearch"
                    :class="{ 'compact-design': localCompactView }"
                >
                    <th v-if="hasIndexColumn"></th>
                    <th
                        v-if="hasRowControlColumn()"
                        class="row-control-cell"
                        :style="getRowControlColumnStyle()"
                    ></th>
                    <th
                        v-if="dragHandleVisible"
                        class="row-checkbox-cell"
                        :style="getSelectColumnStyle()"
                    ></th>
                    <th
                        v-if="selectSingle && !selectMultiple && hasVisibleColumns"
                        class="row-checkbox-header"
                        :style="[
                            getSelectColumnStyle(),
                            {
                                position: stickyHeader ? 'sticky' : 'initial',
                                'z-index': 9,
                            },
                        ]"
                    ></th>
                    <!-- <th v-else-if="selectMultiple || exportUrl" class="row-checkbox-header"></th> -->
                    <th
                        v-else-if="selectMultiple && hasVisibleColumns && hasSelectableRecord"
                        class="row-checkbox-header"
                        :style="[
                            getSelectColumnStyle(),
                            {
                                position: stickyHeader ? 'sticky' : 'initial',
                                'z-index': 9,
                            },
                        ]"
                    ></th>

                    <template v-for="column in columns">
                        <th
                            :key="column.id"
                            v-if="!column.disabled"
                            v-show="column.visible"
                            :class="[column.type + '-header', column.additional_classes]"
                            :style="[
                                {
                                    position: stickyHeader ? 'sticky' : 'initial',
                                    'z-index': 10,
                                },
                            ]"
                        >
                            <div class="table-header-wrapper">
                                <slot :name="`${column.id}_column_header_content`">
                                    <template v-if="false === column.searchable"></template>

                                    <bo-date-range
                                        v-else-if="'datetime' == column.type"
                                        :id="column.id + '_header_datepicker'"
                                        :editable="true"
                                        :start="column.search ? column.search[0] : null"
                                        :end="column.search ? column.search[1] : null"
                                        :separator="column.separator"
                                        :defaultStartText="column.defaultStartText"
                                        :defaultEndText="column.defaultEndText"
                                        :defaultEmptyText="column.defaultEmptyText"
                                        :showSeparatorIfEmpty="column.showSeparatorIfEmpty"
                                        @change="
                                            column.search = $event;
                                            updateDateSearch(column);
                                        "
                                    >
                                    </bo-date-range>

                                    <bo-date-range
                                        v-else-if="'date_range' == column.type"
                                        :id="column.id + '_header_datepicker'"
                                        :editable="true"
                                        :start="column.search ? column.search[0] : null"
                                        :end="column.search ? column.search[1] : null"
                                        :separator="column.separator"
                                        :defaultStartText="column.defaultStartText"
                                        :defaultEndText="column.defaultEndText"
                                        :defaultEmptyText="column.defaultEmptyText"
                                        :showSeparatorIfEmpty="column.showSeparatorIfEmpty"
                                        @change="
                                            column.search = $event;
                                            updateDateSearch(column);
                                        "
                                    >
                                    </bo-date-range>

                                    <bo-search-number
                                        v-else-if="
                                            column.numberSearch !== false &&
                                            ('number' == column.type ||
                                                'currency' == column.type ||
                                                'number2' == column.type)
                                        "
                                        v-on:updateSearch="updateSearch(column)"
                                        :column="column"
                                        :id="column.id + '_header_search'"
                                        :parentId="id"
                                        :rightPos="pointsAtLast"
                                    >
                                    </bo-search-number>

                                    <bo-select-with-search
                                        v-else-if="'select_with_search' === column.searchType"
                                        :id="column.id + '_header_dropdown'"
                                        :items="searchSelectItems(column)"
                                        :value="searchSelectValue(column)"
                                        :option-placeholder="generalTranslations.all ?? 'All'"
                                        :placeholder="generalTranslations.search ?? 'Search'"
                                        @change="onSearchSelectChange(column, $event)"
                                    ></bo-select-with-search>

                                    <bo-select
                                        id="status_change_dropdown"
                                        v-else-if="
                                            ('select' == column.type || 'status' == column.type) &&
                                            column.searchType !== 'text'
                                        "
                                        v-model="column.search"
                                        :id="column.id + '_header_dropdown'"
                                        :options="column.options"
                                        :labels="column.labels"
                                        :multiple="column.multiple"
                                        :nullable="true"
                                        :nullText="generalTranslations.all ?? 'All'"
                                        @change="updateSearch(column)"
                                    >
                                    </bo-select>

                                    <!-- <template v-else-if="'product' == column.type && true !== withoutProductAdvanceSearch">
                                    <bo-search-product
                                        :ref="'search_' + column.id"
                                        :column="column"
                                        :parentId="id"
                                        v-on:searchProduct="updateSearch"
                                        v-on:advanceSearch="advanceSearch"
                                        :advanceSearchProductUrl="advanceSearchProductUrl"
                                        :advanceSearchProductOptionsUrl="advanceSearchProductOptionsUrl"
                                        :advanceSearchColumns="advanceSearchColumns"
                                    ></bo-search-product>
                                    </template> -->

                                    <template v-else-if="'attachment' == column.type"> </template>

                                    <bo-select
                                        v-else-if="'toggle' == column.type"
                                        v-model="column.search"
                                        :id="column.id + '_header_dropdown'"
                                        :options="column.options"
                                        :labels="column.labels"
                                        :nullable="true"
                                        :nullText="generalTranslations.all ?? 'All'"
                                        @change="updateSearch(column)"
                                    >
                                    </bo-select>
                                    <!-- normal textbox -->
                                    <input
                                        v-else
                                        v-model="column.search"
                                        class="search-input"
                                        type="text"
                                        :id="column.id + '_header_search'"
                                        :field="column.id"
                                        :placeholder="generalTranslations.search ?? 'Search'"
                                        @change="updateSearch(column)"
                                        @focus="changePopupVisibility($event, column)"
                                    />

                                    <template v-if="'checkbox' == column.type && true == column.check_column">
                                        <div class="checkbox-header">
                                            <input
                                                :id="column.id + '_header_checkbox'"
                                                type="checkbox"
                                                v-model="column.checked"
                                                @change="checkColumnRecords($event, column)"
                                            />
                                        </div>
                                    </template>
                                </slot>

                                <div
                                    :id="column.id + '_header_custom_filter'"
                                    class="custom-filter"
                                    v-if="column.customFilter"
                                    v-click-outside="(event) => handleCustomFilterClickedOutside(column, event)"
                                >
                                    <div
                                        class="filter-icon-container cursor-pointer"
                                        @click="customFilter(column, true)"
                                    >
                                        <i
                                            :class="
                                                isCustomFilterSelected(column, 'containExactly') ||
                                                isCustomFilterSelected(column, 'isEmpty') ||
                                                isCustomFilterSelected(column, 'isNotEmpty')
                                                    ? 'icon-Status danger'
                                                    : ''
                                            "
                                        ></i>
                                        <i class="icon icon-filteralt"></i>
                                    </div>
                                </div>
                                <div
                                    id="custom_filter_dropdown_menu_right"
                                    class="dropdown-menu dropdown-menu-right"
                                    style="display: block"
                                    v-if="customFilters[column.id]"
                                    v-click-outside="hideHeaderFilters"
                                >
                                    <div
                                        :class="[
                                            isCustomFilterSelected(column, 'containExactly') ? 'active selected' : '',
                                            'dropdown-item',
                                        ]"
                                        @click="customFilterFunction(column, 'containExactly')"
                                    >
                                        {{ generalTranslations.contains_exactly ?? "Contains Exactly" }}
                                    </div>
                                    <div
                                        :class="[
                                            isCustomFilterSelected(column, 'isEmpty') ? 'active selected' : '',
                                            'dropdown-item',
                                        ]"
                                        @click="customFilterFunction(column, 'isEmpty')"
                                    >
                                        {{ generalTranslations.is_empty ?? "Is Empty" }}
                                    </div>
                                    <div
                                        :class="[
                                            isCustomFilterSelected(column, 'isNotEmpty') ? 'active selected' : '',
                                            'dropdown-item',
                                        ]"
                                        @click="customFilterFunction(column, 'isNotEmpty')"
                                    >
                                        {{ generalTranslations.is_not_empty ?? "Is Not Empty" }}
                                    </div>
                                    <div class="divider"></div>
                                    <div
                                        id="column_filter_reset_button"
                                        :class="[
                                            isCustomFilterSelected(column, '') ? 'active selected' : '',
                                            'reset-text cursor-pointer',
                                        ]"
                                        @click="customFilterFunction(column, '')"
                                    >
                                        <i class="icon icon-cached"></i>
                                        {{ generalTranslations.reset ?? "Reset" }}
                                    </div>
                                </div>
                            </div>
                        </th>
                    </template>
                    <th
                        v-if="displayActionColumn"
                        class="row-action-cell"
                    ></th>
                    <th style="min-width: 1px">
                        <!-- spacer -->
                    </th>
                </tr>

                <!-- additional header if have-->
                <tr
                    v-if="additionalHeader && additionalHeader.length > 0"
                    :class="{ 'compact-design': localCompactView }"
                >
                    <th v-if="hasIndexColumn"></th>
                    <th
                        v-if="hasRowControlColumn()"
                        class="row-control-cell"
                        :style="getRowControlColumnStyle()"
                    ></th>
                    <th
                        v-if="dragHandleVisible"
                        class="row-checkbox-cell"
                        :style="getSelectColumnStyle()"
                    ></th>
                    <th
                        v-if="selectSingle && !selectMultiple && hasVisibleColumns"
                        class="row-checkbox-header"
                        :style="[
                            getSelectColumnStyle(),
                            {
                                position: stickyHeader ? 'sticky' : 'initial',
                                'z-index': 9,
                            },
                        ]"
                    ></th>
                    <th
                        v-if="selectMultiple && hasVisibleColumns && hasSelectableRecord"
                        class="row-checkbox-header"
                        :style="[
                            getSelectColumnStyle(),
                            {
                                position: stickyHeader ? 'sticky' : 'initial',
                                'z-index': 9,
                            },
                        ]"
                    >
                        <input
                            type="checkbox"
                            :id="formattedDatatableUniqueKey + '_header_checkbox'"
                            v-model="headerChecked"
                            :indeterminate.prop="isHeaderIndeterminate"
                            @change="checkAllRecords"
                        />
                    </th>
                    <th
                        v-for="header in additionalHeader"
                        v-bind="header"
                    >
                        {{ header.label }}
                    </th>
                    <th
                        v-if="displayActionColumn"
                        class="row-action-cell"
                    ></th>
                    <th style="min-width: 1px">
                        <!-- spacer -->
                    </th>
                </tr>

                <tr :class="{ 'enable-search': enableSearch, 'compact-design': localCompactView }">
                    <th v-if="hasIndexColumn"></th>
                    <th
                        v-if="hasRowControlColumn()"
                        class="row-control-cell"
                        :style="getRowControlColumnStyle()"
                    ></th>
                    <th
                        v-if="dragHandleVisible"
                        class="row-checkbox-cell"
                        :style="getSelectColumnStyle()"
                    ></th>
                    <th
                        v-if="selectSingle && !selectMultiple && hasVisibleColumns"
                        class="row-checkbox-header"
                        :style="[
                            getSelectColumnStyle(),
                            {
                                position: stickyHeader ? 'sticky' : 'initial',
                                'z-index': 9,
                            },
                        ]"
                    ></th>
                    <th
                        v-if="selectMultiple && hasVisibleColumns && hasSelectableRecord"
                        class="row-checkbox-header"
                        :style="[
                            getSelectColumnStyle(),
                            {
                                position: stickyHeader ? 'sticky' : 'initial',
                                'z-index': 9,
                            },
                        ]"
                    >
                        <input
                            type="checkbox"
                            :id="formattedDatatableUniqueKey + '_header_checkbox'"
                            v-model="headerChecked"
                            :indeterminate.prop="isHeaderIndeterminate"
                            @change="checkAllRecords"
                        />
                    </th>
                    <template v-for="column in columns">
                        <th
                            @click="sort(column)"
                            :id="column.id + '_header_sort'"
                            :key="column.id"
                            :class="[
                                column.additional_classes,
                                column.type + '-header',
                                {
                                    sorting: column.orderable,
                                    sorting_asc: sortColumn == column.id && dir == 'asc',
                                    sorting_desc: sortColumn == column.id && dir == 'desc',
                                },
                            ]"
                            v-if="!column.disabled"
                            v-show="column.visible"
                            :style="[
                                {
                                    position: stickyHeader ? 'sticky' : 'initial',
                                    'z-index': 9,
                                },
                            ]"
                        >
                            <span
                                :id="column.id + '_header_text'"
                                :class="'checkbox' == column.type ? 'checkbox-header' : ''"
                                >{{ column.label }}
                                <i
                                    v-if="column.show_tooltip"
                                    class="icon-helpcircle"
                                    data-toggle="tooltip"
                                    data-container="body"
                                    data-boundary="window"
                                    :data-placement="column.tooltip_placement ?? 'bottom'"
                                    :title="column.tooltip_text ?? ''"
                                    @click.stop
                                    @mousedown.stop
                                ></i>
                            </span>
                        </th>
                    </template>
                    <th
                        v-if="displayActionColumn"
                        class="row-action-cell"
                    >
                        {{ generalTranslations.action ?? "Action" }}
                    </th>
                    <th style="min-width: 1px">
                        <div>&nbsp;</div>
                        <!-- spacer -->
                    </th>
                </tr>
            </thead>

            <!-- Datatable body -->
            <draggable
                slot="body"
                tag="tbody"
                v-model="recordData"
                v-bind="dragOptions"
                :disabled="!dragHandleVisible"
            >
                <!-- <template #item="{ element: record, index }"> -->
                <!-- <template slot="item" slot-scope="{ element: record, index }"> -->
                <bo-datatable-row
                    v-for="(record, index) in recordData"
                    :key="record[primaryKey]"
                    :record="record"
                    :index="index"
                    :selected="isSelected(record[primaryKey].toString())"
                ></bo-datatable-row>

                <!-- <template
                        v-if="record.subrecords && record.subrecords.length && record.subrecordVisibility === true"
                    >
                        <bo-datatable-subrecord
                            v-for="subrecord in record.subrecords"
                            :id="subrecordID"
                            :prepend="selectMultiple"
                            :key="record[primaryKey] + '_' + subrecord[primaryKey]"
                            :columns="subrecordColumns"
                            :item="subrecord"
                            :enableDeleteRecord="enableDeleteRecord && deleteSubrecord"
                            @change="subrecordChangeHandler(record[primaryKey], subrecord[primaryKey], ...arguments)"
                            @delete="subrecordDeleteHandler(record[primaryKey], subrecord[primaryKey], ...arguments)"
                            @loading="showLoading"
                            @done_loading="hideLoading"
                        ></bo-datatable-subrecord>
                    </template> -->
                <!-- </template> -->

                <template v-if="Object.values(recordData).length <= 0 || noResultsFiltered">
                    <tr
                        class="bo-empty-data"
                        :class="{ 'compact-design': localCompactView }"
                    >
                        <td
                            :colspan="Object.values(columns).length"
                            class="no-data"
                        >
                            <div class="no-result-found">
                                <bo-svg
                                    :svg-content="'/assets/graphics/img-no-data.svg'"
                                    :primary-color="primaryColor"
                                ></bo-svg>
                                <div class="title">
                                    {{ generalTranslations.no_results_found ?? "No results found" }}
                                </div>
                            </div>
                        </td>
                    </tr>
                    <!-- <tr
                        class="bo-empty-data"
                        :class="{ 'compact-design': localCompactView }"
                    >
                        <td
                            class="no-data"
                            :colspan="Object.values(columns).length"
                        >
                            <bo-empty-data
                                :title="generalTranslations.no_results_found"
                                :showButton="false"
                            >
                            </bo-empty-data>
                        </td>
                    </tr> -->
                </template>
            </draggable>
            <!-- </tbody> -->
            <!-- Datatable footer -->
            <div slot="pagination"></div>
        </data-table>

        <div
            :class="['bo-context-menu', 'corner-menu', 'error-menu', { show: errorContextMenu }]"
            :style="errorContextMenuPosition"
        >
            <div class="context-item">
                {{ errorContextMenuMessage }}
            </div>
        </div>

        <!-- end of popup section -->
        <slot name="footer"></slot>

        <div
            v-if="pagination"
            class="table-footer bo-table-footer has-pagination"
        >
            <div class="total">
                <div class="total-count title">
                    {{ showPagination(records, pagination) }}
                </div>
                <div class="rows-per-page-total">
                    {{ generalTranslations.rows ?? "Rows" }}

                    <!-- have watcher -->
                    <select
                        id="select_page_dropdown"
                        v-model="limitPerPage"
                    >
                        <option
                            v-for="(limit, index) in perPage"
                            :key="index"
                        >
                            {{ limit }}
                        </option>
                    </select>
                </div>
            </div>
            <div class="pagination">
                <div class="rows-per-page">
                    <span>
                        {{ generalTranslations.rows ?? "Rows" }}
                    </span>

                    <!-- have watcher -->
                    <select
                        id="select_page_dropdown"
                        v-model="limitPerPage"
                    >
                        <option
                            v-for="(limit, index) in perPage"
                            :key="index"
                        >
                            {{ limit }}
                        </option>
                    </select>
                </div>

                <div class="page-navigation">
                    <div class="pages">
                        <span
                            v-for="(link, index) in computedPaginationLinks"
                            :key="index"
                        >
                            <a
                                v-if="link.label == 'pagination.previous'"
                                id="previous_page_button"
                                :key="link.label"
                                @click="changePage(link.url)"
                                :class="{ active: link.active }"
                            >
                                <i class="icon-chevronleft"></i>
                            </a>

                            <a
                                v-else-if="link.label == 'pagination.next'"
                                id="previous_page_button"
                                @click="changePage(link.url)"
                                :class="{ active: link.active }"
                            >
                                <i class="icon-chevronright"></i>
                            </a>

                            <a
                                v-else
                                :id="'_change_to_page_' + link.label + '_button'"
                                @click="changePage(link.url)"
                                :class="{ active: link.active }"
                            >
                                {{ link.label }}
                            </a>
                        </span>
                    </div>

                    <div class="go-to">
                        <span>
                            {{ generalTranslations.page ?? "Page" }}
                        </span>
                        <input
                            type="number"
                            v-model="pageNumber"
                        />
                        <button
                            id="go_to_page_button"
                            class="btn-go"
                            @click="goToPage()"
                            :disabled="pageNumber == ''"
                        >
                            <nobr>{{ generalTranslations.go ?? "Go" }}</nobr>
                        </button>
                    </div>
                </div>
            </div>
        </div>
        <!--
    <bo-datatable-footer
      :getRecordsUrl.sync="getRecordsUrl"
      :modified="modified"
      :dataUrl="dataUrl"
      :tableFooter="tableFooter"
      ref="footer"
    ></bo-datatable-footer> -->
        <bo-confirmation
            :custom-modal-backdrop="boDataTableConfirmationCustomModalBackdrop"
            :cancel="boDataTableConfirmationCancel"
            ref="bo_confirmation"
        >
        </bo-confirmation>
    </div>
</template>

<script>
import Vue from "vue";
import axios from "axios";
import DataTable from "laravel-vue-datatable";
Vue.use(DataTable);
import draggable from "vuedraggable";
import DatePicker from "vue2-datepicker";
import "vue2-datepicker/index.css";
import VueNumeric from "vue-numeric";
import moment, { max } from "moment";
import VueSlider from "vue-slider-component";
import "vue-slider-component/theme/default.css";
import vClickOutside from "v-click-outside";
Vue.use(vClickOutside);
import VueLazyLoad from "vue-lazyload";
Vue.use(VueLazyLoad);
import boConfirmation from "@/v2/components/bo-confirmation.vue";
import datatableRow from "@/v2/components/bo-datatable-row.vue";
import overviewTableHeightMixin from "@/v2/mixins/overview-table-height.js";
Vue.component("bo-confirmation", boConfirmation);
import boDatatableRow from "@/bo-datatable/bo-datatable-subrecord.vue";
Vue.component("bo-datatable-subrecord", boDatatableRow);
import boCellProduct from "@/bo-datatable/bo-cell-product.vue";
Vue.component("bo-cell-product", boCellProduct);
import boCellMultiselect from "@/bo-datatable/bo-cell-multiselect.vue";
Vue.component("bo-cell-multiselect", boCellMultiselect);
import boCellPerTotal from "@/bo-datatable/bo-cell-per-total.vue";
Vue.component("bo-cell-per-total", boCellPerTotal);
import boCellAttachment from "@/bo-datatable/bo-cell-attachment.vue";
Vue.component("bo-cell-attachment", boCellAttachment);
import boSelect from "@/v2/components/bo-select.vue";
Vue.component("bo-select", boSelect);
import boSelectWithSearch from "@/v2/components/bo-select-with-search.vue";
Vue.component("bo-select-with-search", boSelectWithSearch);
import boCellHtml from "@/bo-datatable/bo-cell-html.vue";
Vue.component("bo-cell-html", boCellHtml);
import boCellStatus from "@/bo-datatable/bo-cell-status.vue";
Vue.component("bo-cell-status", boCellStatus);
import boCellNumber from "@/v2/components/bo-cell-number.vue";
Vue.component("bo-cell-number", boCellNumber);
import boCellCurrency from "@/v2/components/bo-cell-currency.vue";
Vue.component("bo-cell-currency", boCellCurrency);
import boCellDatetime from "@/v2/components/bo-cell-datetime.vue";
Vue.component("bo-cell-datetime", boCellDatetime);
import boDateRange from "@/v2/components/bo-date-range.vue";
Vue.component("bo-date-range", boDateRange);
import boCellLink from "@/bo-datatable/bo-cell-link.vue";
Vue.component("bo-cell-link", boCellLink);
import boCellUser from "@/bo-datatable/bo-cell-user.vue";
Vue.component("bo-cell-user", boCellUser);
import boSearchProduct from "@/bo-datatable/bo-search-product.vue";
Vue.component("bo-search-product", boSearchProduct);
import boSearchNumber from "@/v2/components/bo-search-number.vue";
Vue.component("bo-search-number", boSearchNumber);
import boCellModeInputNumber from "@/bo-datatable/bo-cell-mode-input-number.vue";
Vue.component("bo-cell-mode-input-number", boCellModeInputNumber);
import boAdvanceSearch from "@/bo-datatable/bo-advance-search.vue";
Vue.component("bo-advance-search", boAdvanceSearch);
import boCellToggle from "@/v2/components/bo-cell-toggle.vue";
Vue.component("bo-cell-toggle", boCellToggle);
import boCellCustomToggle from "@/v2/components/bo-cell-custom-toggle.vue";
Vue.component("bo-cell-custom-toggle", boCellCustomToggle);
Vue.component("bo-cell-button-popup", require("@/v2/components/bo-cell-button-popup.vue").default);
Vue.component("bo-datatable-new", require("@/v2/components/bo-datatable-new.vue").default);
import boSelectPopup from "@/v2/components/bo-select-popup.vue";
Vue.component("bo-select-popup", boSelectPopup);
Vue.component("bo-svg", require("js/frontend/components/bo-svg.vue").default);
Vue.component("bo-list-box", require("@/v2/components/bo-list-box.vue").default);

import { confirmationMixin } from "@/v2/mixins/bo-confirmation.js";
import { Prompt } from "@/v2/models/bo-confirmation.js";
import httpMixin from "@/v2/mixins/http.js";
import columnPreferenceMixin from "@/v2/mixins/column-preference.js";
import { boDatatableNewMixin } from "@/v2/mixins/bo-datatable-new.js";

axios.defaults.headers.common["X-CSRF-TOKEN"] = document.getElementsByName("X-CSRF-TOKEN")[0].getAttribute("content");

export default {
    inject: {
        triggerPopupModalAction: {
            from: "triggerPopupModalAction",
            default: () => {},
        },
    },
    // Expose this datatable instance to extracted <bo-datatable-row> children so a
    // single row can delegate to the datatable's methods / state without a large
    // prop surface. See bo-datatable-row.vue.
    provide() {
        return {
            datatableCtx: this,
        };
    },
    mixins: [confirmationMixin, httpMixin, columnPreferenceMixin, boDatatableNewMixin, overviewTableHeightMixin],
    props: {
        id: {
            type: String,
            default: "",
        },
        primaryKey: {
            type: String,
            default: "id",
        },
        title: {
            type: String,
            default: "",
        },
        editable: {
            type: Boolean,
            default: true,
        },
        // Callback to determine if a record is editable. Accepts a record and column as parameters
        recordEditable: {
            type: Function,
            default: null,
        },
        // Callback to determine if a record is selectable. Accepts a record as parameter
        recordSelectable: {
            type: Function,
            default: null,
        },
        selectSingle: {
            type: Boolean,
            default: false,
        },
        selectMultiple: {
            type: Boolean,
            default: false,
        },
        draggable: {
            type: Boolean,
            default: false,
        },
        showSortFilterStatusBar: {
            type: Boolean,
            default: false,
        },
        selectRecordPrefix: {
            type: String,
            default: "",
        },
        selectRowConditions: {
            type: [Object, Array],
            default: () => ({}),
        },
        //Columns, records, subrecords all have to be indexed by primary key
        columns: {
            type: Object,
            default: () => ({}),
        },
        subrecordColumns: {
            type: Array,
            default: () => [],
        },
        deleteSubrecord: {
            type: Boolean,
            default: true,
        },
        displayTitleHeader: {
            type: Boolean,
            default: true,
        },
        showHeader: {
            type: Boolean,
            default: true,
        },
        skipPortal: {
            type: Boolean,
            default: false,
        },
        // Same contract as columns above and as bo-datatable's own records prop: normally an object
        // indexed by primary key, which is what the component is written for (orderRecords, the
        // for..in in selectedRecords, Object.values in recordData). A plain array is also accepted
        // because a handful of pages pass one - error message lists and related-document lists.
        records: {
            type: [Array, Object],
            default: () => [],
        },
        pagination: {
            type: Object,
            default: null,
        },
        paginationCount: {
            type: Number,
            default: null,
        },
        headers: {
            type: Object,
            default: () => ({
                current_page: 1,
                per_page: 50,
                total: 0,
                last_page: 1,
                from: 1,
                to: 0,
                links: [],
            }),
        },
        defaultLimit: {
            type: Number,
            default: 50,
        },
        enableUpdateRecord: {
            type: Boolean,
            default: false,
        },
        enableDeleteRecord: {
            type: Boolean,
            default: false,
        },
        enableDeleteSingleRecord: {
            type: Boolean,
            default: false,
        },
        enableDeleteMultipleRecords: {
            type: Boolean,
            default: false,
        },
        enableEditRecord: {
            type: Boolean,
            default: false,
        },
        enableEditRecordMultiple: {
            type: Boolean,
            default: true,
        },
        enableEditRecordModal: {
            type: Boolean,
            default: false,
        },
        enableViewRecord: {
            type: Boolean,
            default: false,
        },
        enableViewLogRecord: {
            type: Boolean,
            default: false,
        },
        enableViewSelectedRecords: {
            type: Boolean,
            default: true,
        },
        // Whether the table allow search
        enableSearch: {
            type: Boolean,
            default: true,
        },
        // Whether to use external prompting mechanisms to prevent double prompting
        // If true, then a prompt event will be fired
        externalPrompt: {
            type: Boolean,
            default: false,
        },
        // Override existing component options with runtime mixins
        mixins: {
            type: Array,
            default: null,
        },
        localSearchSort: {
            type: Boolean,
            default: false,
        },
        hideRefreshButton: {
            type: Boolean,
            default: true,
        },
        hideDisplayHeaderFilter: {
            type: Boolean,
            default: false,
        },
        printUrl: {
            type: String,
            default: "",
        },
        isForm: {
            type: Boolean,
            default: false,
        },
        isOverview: {
            type: Boolean,
            default: false,
        },
        requiredTable: {
            type: Boolean,
            default: false,
        },
        requiredMessage: {
            type: String,
            default: "",
        },
        rowWarningOptions: {
            type: Object,
            default: function () {
                return {};
            },
        },
        headerLeftMenu: {
            type: Array,
            default: null,
        },
        headerRightMenu: {
            type: Array,
            default: null,
        },
        customDetailsPageUrl: {
            type: String,
            default: "",
        },
        customRecordsPageUrl: {
            type: String,
            default: "",
        },
        hasIndexColumn: {
            type: Boolean,
            default: false,
        },
        additionalHeader: {
            type: Array,
            default: null,
        },
        isCollapsible: {
            type: Boolean,
            default: false,
        },
        tableId: {
            type: String,
            default: "",
        },
        columnPreferenceUrl: {
            type: String,
            default: "",
        },
        datatableUniqueKey: {
            type: String,
            default: null,
        },
        sortColumnProp: {
            type: String,
            default: "",
        },
        dirProp: {
            type: String,
            default: "",
        },
        enableColumnPreference: {
            type: Boolean,
            default: true,
        },
        tableStyle: {
            type: String,
            default: "",
        },
        forceShowActionColumn: {
            type: Boolean,
            default: false,
        },
        showActionColumn: {
            type: Boolean,
            default: true,
        },
        showSelectAll: {
            type: Boolean,
            default: false,
        },
        compactView: {
            type: Boolean,
            default: true,
        },
        mappingError: {
            type: Boolean,
        },
        hasFooter: {
            type: Boolean,
            default: false,
        },
        disableClearSelectionWhenLoadingData: {
            type: Boolean,
            default: false,
        },
        disableContextMenu: {
            type: Boolean,
            default: false,
        },
        primaryColor: {
            type: String,
            default: "#3E9DF0",
        },
        calculateOffsetType: {
            type: String,
            default: "",
        },
        stopPropagation: {
            type: Boolean,
            default: false,
        },
        emitOnClick: {
            type: Boolean,
            default: false,
        },
        clearSelectionAfterSearch: {
            type: Boolean,
            default: true,
        },
        clearSelectionAfterSort: {
            type: Boolean,
            default: true,
        },
        disableRow: {
            type: Function,
            default: null,
        },
        mergedRow: {
            type: Function,
            default: null,
        },
        mergedRowColumnId: {
            type: String,
            default: "",
        },
        mergedRowPreservedColumnIds: {
            type: Array,
            default: () => [],
        },
        recordVisible: {
            type: Function,
            default: null,
        },
        rowClass: {
            type: Function,
            default: null,
        },
        selectColumnWidth: {
            type: String,
            default: "",
        },
        rowControlColumnWidth: {
            type: String,
            default: "",
        },
        headerCheckboxIndeterminate: {
            type: Boolean,
            default: true,
        },
        hideSequenceSaveButton: {
            type: Boolean,
            default: false,
        },
        stickyHeader: {
            type: Boolean,
            default: true,
        },
        globalShowViewMoreButton: {
            type: Boolean,
            default: true,
        },
    },

    components: {
        draggable,
        DatePicker,
        VueNumeric,
        VueSlider,
        "bo-datatable-row": datatableRow,
    },

    beforeCreate() {
        const mixins = this.$options.propsData.mixins;
        if (mixins) {
            mixins.forEach((mixin) => {
                this.$options = Vue.util.mergeOptions(this.$options, mixin, this);
            });
        }
    },

    data() {
        return {
            // Table header properties
            // stickyHeader: true,
            headerChecked: false,
            // Header popup property
            displayHeaderFilters: false,
            // Used for advanced search
            // headerPopupPositionTop: 0,
            // headerPopupPositionLeft: 0,
            // headerPopupPositionLeftDefault: 0,

            // Header sorting property
            allHeadersSelected: false,
            drag: false,
            localDraggable: true,

            // Table footer properties
            perPage: [20, 50, 100, 200, 300, 400, 500],
            totalRecords: 0,
            pageNumber: "",
            page: 1,
            limit: this.defaultLimit,
            search: {},
            // sortColumn: {},
            sortColumn: null,
            dir: "desc",

            // Table properties
            defaultColumns: this.columns,
            // Selected records
            selectedRecord: null, // Radio button
            // selectableRowConditions: [],
            // selectablePrefix: null, // Use prop selectRecordPrefix
            // Modified records
            modified: false,
            // addedRecords: [],
            // deletedRecords: [],
            // modifiedRecords: [], // Contains added and edited records
            // Toggle invisibility
            visible: true,

            // Whether can updata by row
            // Use slots for these
            // updateRowButtonText: this.$attrs["update-row-button-text"],
            // updateRowConfirmationButtonText:
            //     this.$attrs["update-row-confirmation-button-text"],

            // Use events for this
            // updateRowUrl: this.$attrs["update-row-url"],

            // advance search property
            // Use slots
            // advanceSearchColumns: this.$attrs["search-product-columns"],
            // advanceSearchProductUrl: this.$attrs["search-product-url"],
            // advanceSearchProductOptionsUrl:
            //     this.$attrs["search-product-options-url"],
            // withoutProductAdvanceSearch:
            //     this.$attrs["without-product-advance-search"],

            // additional data passed by the controller
            // Use computed property
            // miscData: {},
            window: window,
            showMenu: false,
            menuPosition: { x: 0, y: 0 },
            menuHeight: null,
            menuWidth: null,
            selectedRow: null,
            contextMenuPosition: { top: 0, left: 0 },
            selectedRecordKeys: [],
            displayBarEnable: false,
            isPostingColumnPreferenceEnabled: true,

            popupModalColumns: null,
            popupModalRecords: {},
            popupModalPagination: null,

            errorContextMenu: false,
            errorContextMenuPosition: { left: "0px", top: "0px" },
            errorContextMenuMessage: "",

            objectId: "",

            localCompactView: this.compactView,
            totalAllRecords: 0,
            selectedAllRecords: false,
            customFilters: {},
            customFilterValues: {},

            boDataTableConfirmationCustomModalBackdrop: false,
            boDataTableConfirmationCancel: true,
            pointsAtLast: false,

            showViewMoreButton: false,
            previousShowViewMoreButtonId: null,
            previousShowViewMoreButtonColumn: null,
            tooltipVisible: false,
            previousTooltipId: null,
            previousTooltipColumn: null,
            noResultsFiltered: false,

            // translation
            generalTranslations: window.bo?.generalTranslations ?? {},
            change_page_string: window.bo?.generalTranslations?.change_page ?? "Change Page",
            change_page_message_string:
                (window.bo?.generalTranslations?.change_page_question ??
                    "Changes you made may not be saved. Want to proceed?") +
                "<br>" +
                (window.bo?.generalTranslations?.action_cannot_undone ?? "This action cannot be undone"),
            proceed_string: window.bo?.generalTranslations?.proceed ?? "Proceed",
            delete_records_string: window.bo?.generalTranslations?.delete_records ?? "Delete Records",
            delete_records_message_string:
                (window.bo?.generalTranslations?.delete_records_question ??
                    "Are you sure you want to delete the following records?") +
                "<br>" +
                (window.bo?.generalTranslations?.action_cannot_undone ?? "This action cannot be undone"),
            delete_string: window.bo?.generalTranslations?.delete ?? "Delete",
        };
    },

    computed: {
        isHeaderIndeterminate() {
            return this.selectedRecords.length > 0 && !this.headerChecked;
        },
        isIndeterminate() {
            const totalColumn = this.columnSequence.filter((header) => header.disabled === undefined).length;
            // Disabled columns are not in the picker, so they must not count as visible either.
            const visibleAndHasLabelCount = this.columnSequence.filter(
                (header) => header.disabled === undefined && header.visible === true && header.label != ""
            ).length;
            if (visibleAndHasLabelCount == totalColumn) this.allHeadersSelected = true;
            if (visibleAndHasLabelCount == 0) this.allHeadersSelected = false;

            return visibleAndHasLabelCount > 0 && visibleAndHasLabelCount < totalColumn;
        },
        tableClasses() {
            return {
                "table-container": {
                    "table-responsive": false,
                    dataTables_wrapper: true,
                    "no-footer": true,
                },
                table: {
                    table: false,
                    "table-striped": false,
                    border: false,
                    "has-filter": true,
                    dataTable: true,
                    "no-footer": true,
                    "has-footer": this.hasFooter,
                    "no-data": Object.values(this.recordData).length <= 0,
                },
                "t-head": {},
                "t-body": {},
                "t-head-tr": {},
                "t-body-tr": {},
                td: {},
                th: {},
            };
        },

        dragOptions() {
            return {
                animation: 200,
                group: "description",
                disabled: false,
                ghostClass: "ghost",
            };
        },

        tableTitle() {
            // is any row selected event is used to hide/show elements in header such as create new document button and status dropdown
            // Changes title whenever any row is selected to indicate number of selected documents
            // if (this.selectedRecordsCount > 0) {
            //     return this.selectedRecords.length.toString() + " Selected";
            // } else {
            //     return this.title;
            // }

            return this.title;
        },

        recordData: {
            get() {
                let arr = Array.isArray(this.records) ? this.records : this.orderRecords(this.records);
                arr.sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0));
                return arr;
            },
            set(newValue) {
                // Local-mode column sort only reorders the DOM, not this array, so the new
                // sequence has to be read off the actual row order in the table.
                const applyDomOrderAndEmit = () => {
                    let table = this.$el.querySelector("table");
                    let rows = table.querySelectorAll("tbody > tr");
                    rows.forEach((row, index) => {
                        const id = row.id.split("_")[1];
                        const item = newValue.find((i) => i.id == id);

                        if (item && "sequence" in item) {
                            item.sequence = index + 1;
                        }
                    });
                    newValue.sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0));
                    this.sortColumn = null;
                    this.emitGenericEvent("tableRow_sequence_change", newValue);
                };

                if (this.sortColumn && this.draggable) {
                    if (this.showSortFilterStatusBar) {
                        // Opt-in (this ticket only): dragging never saves to the server, it
                        // only refreshes the in-memory sequence, so no confirmation is needed.
                        applyDomOrderAndEmit();
                    } else {
                        // Original behavior, unchanged for every other existing consumer of
                        // this shared component.
                        this.$refs.bo_confirmation
                            .activate(
                                this.generalTranslations.update_records,
                                this.generalTranslations.update_sequence_message,
                                this.generalTranslations.confirm,
                                this.generalTranslations.cancel,
                                "success"
                            )
                            .then((confirm) => {
                                if (confirm) {
                                    applyDomOrderAndEmit();
                                }
                            });
                    }
                } else {
                    newValue.forEach((item, index) => {
                        if ("sequence" in item) {
                            item.sequence = index + 1;
                        }
                    });
                    this.sortColumn = null;
                    this.emitGenericEvent("tableRow_sequence_change", newValue);
                }
            },
        },

        dragHandleVisible() {
            // For consumers that opt into the sort/filter status bar, dragging only makes
            // sense against the manual (unsorted, unfiltered) order — sorting by any other
            // column changes the visible row order independently of `sequence`, so the handle
            // is hidden and "Apply As Sequence" becomes the way to turn that view into the new
            // order. Sorting by the `sequence` column itself is just a labelled view of the
            // existing manual order though, so dragging stays available in that case. Other
            // existing consumers of this component keep their original behavior (drag allowed
            // while sorted, only blocked while filtered).
            const sortedColumnIsSequence = this.sortColumn && this.columns[this.sortColumn]?.for_sequence;
            const blockedBySort = this.showSortFilterStatusBar && this.sortColumn && !sortedColumnIsSequence;

            return (
                this.editable &&
                this.draggable &&
                this.localDraggable &&
                !blockedBySort &&
                !Object.values(this.search).length
            );
        },

        sortedColumnLabel() {
            if (!this.sortColumn) {
                return "";
            }

            const label = this.columns[this.sortColumn] ? this.columns[this.sortColumn].label : this.sortColumn;

            return `${label} (${this.dir == "asc" ? "A>Z" : "Z>A"})`;
        },

        selectedRecords() {
            let records = [];
            for (const key in this.records) {
                if (this.records[key].checked) {
                    records.push(key);
                }
            }
            return records;
        },

        selectedRecordsCount() {
            return this.selectedRecords.length;
        },

        hasSubrecord() {
            return false;
            return (
                this.records && this.records.findIndex((element) => typeof element["subrecords"] !== "undefined") !== -1
            );
        },

        isDataLoaded() {
            return this.records != null;
        },

        limitPerPage: {
            get() {
                return this.limit;
            },
            set(newValue) {
                this.limit = newValue;
                this.page = 1;
                this.getData();
            },
        },

        // Use slots
        // attachmentToDownload() {
        //     return (
        //         -1 ===
        //         this.records.findIndex((element) => {
        //             return element.attachment_url != null;
        //         })
        //     );
        // },

        displayActionColumn() {
            // return this.editable && (this.enableDeleteRecord || this.attachmentToDownload);
            if (this.forceShowActionColumn == true) {
                return this.forceShowActionColumn;
            }
            return (
                this.editable &&
                (this.deleteSingle ||
                    this.deleteMultiple ||
                    this.editSingle ||
                    this.viewSingle ||
                    this.enableEditRecordModal) &&
                this.showActionColumn
            );
        },

        deleteMultiple() {
            return this.enableDeleteRecord || this.enableDeleteMultipleRecords;
        },

        deleteSingle() {
            return this.enableDeleteRecord || this.enableDeleteSingleRecord;
        },

        editSingle() {
            return this.enableEditRecord;
        },

        editMultiple() {
            return this.enableEditRecordMultiple || this.selectedRecords?.length <= 1;
        },

        viewSingle() {
            return this.enableViewRecord;
        },

        viewLogSingle() {
            return this.enableViewLogRecord;
        },

        columnSequence: {
            get() {
                return Object.values(this.columns);
            },
            set(newValue) {
                // for sticky column
                this.saveColumnPreference(newValue);
                this.emitGenericEvent("column_sequence_change", newValue);
            },
        },

        hasVisibleColumns() {
            return this.columnSequence.some((column) => column.visible);
        },

        selectableRecords() {
            if (this.recordSelectable == null) {
                return this.records;
            }

            let temp = {};
            Object.values(this.records)
                .filter((record) => this.isRecordSelectable(record))
                .forEach((record) => {
                    temp[record[this.primaryKey]] = record;
                });

            return temp;
        },

        hasSelectableRecord() {
            return this.selectableRecords != null ? Object.keys(this.selectableRecords).length : 0;
        },

        menuStyles() {
            return {
                top: this.isMenuBeyondBottomEdge
                    ? `${this.menuPosition.y - this.menuHeight}px`
                    : `${this.menuPosition.y}px`,
                left: this.isMenuBeyondRightEdge
                    ? `${this.menuPosition.x - this.menuWidth}px`
                    : `${this.menuPosition.x}px`,
            };
        },
        isMenuBeyondRightEdge() {
            const screenWidth = window.innerWidth;
            return this.menuPosition.x + this.menuWidth > screenWidth;
        },
        isMenuBeyondBottomEdge() {
            const screenHeight = window.innerHeight;
            return this.menuPosition.y + this.menuHeight > screenHeight;
        },
        isSelectedRecordsDeletable() {
            let result = true;

            // cannot delete when selected all records
            if (this.selectedAllRecords) {
                result = false;
            }

            if (this.selectedRecords?.length > 0) {
                let records = this.records;

                this.selectedRecords.forEach((recordId) => {
                    // cannot delete consolidated e-invoice
                    if (
                        records[recordId]?.consolidated_under ||
                        records[recordId]?.is_consolidated ||
                        (typeof records[recordId]?.bo_can_delete != "undefined" &&
                            records[recordId]?.bo_can_delete === false)
                    ) {
                        result = false;
                    }
                });
            }

            return result;
        },

        formattedDatatableUniqueKey() {
            return (
                this.datatableUniqueKey
                    ?.trim()
                    ?.toLowerCase()
                    ?.replace(/[^a-z0-9\-_]/gi, "-") ?? ""
            );
        },

        computedPaginationLinks() {
            if (this.paginationCount == null) {
                return this.pagination.links;
            }

            const current = this.pagination.current_page;
            const last = this.pagination.last_page;
            const count = this.paginationCount;
            const paginationLinks = this.pagination.links;
            const links = [];

            // Extract prev and next from backend
            const prevLink = paginationLinks.find((l) => l.label === "pagination.previous");
            const nextLink = paginationLinks.find((l) => l.label === "pagination.next");

            // Helper function for retrieving links
            const findPagination = (label) => {
                if (label === "...") {
                    return paginationLinks.find((l) => l.label === "...") || { label: "...", url: null, active: false };
                }

                return paginationLinks.find((l) => Number(l.label) === label) || { label, url: null, active: false };
            };

            // Helper function for pushing links
            const pushPagination = (label) => links.push(findPagination(label));

            // Add prev button first
            if (prevLink) links.push(prevLink);

            // ---- Case 1: total pages <= count ----
            if (last <= count) {
                for (let i = 1; i <= last; i++) pushPagination(i);
                if (nextLink) links.push(nextLink);
                return links;
            }

            // ---- Case 2: near start ----
            if (current <= count - 2) {
                for (let i = 1; i <= count; i++) pushPagination(i);
                pushPagination("...");
                pushPagination(last);
                if (nextLink) links.push(nextLink);
                return links;
            }

            // ---- Case 3: near end ----
            if (current >= last - (count - 3)) {
                pushPagination(1);
                pushPagination("...");
                for (let i = last - count + 1; i <= last; i++) pushPagination(i);
                if (nextLink) links.push(nextLink);
                return links;
            }

            // ---- Case 4: middle ----
            const middleCount = count - 2;
            const half = Math.floor(middleCount / 2);

            let start = current - half;
            let end = current + half;

            if (middleCount % 2 === 0) end -= 1;

            pushPagination(1);
            pushPagination("...");

            for (let i = start; i <= end; i++) pushPagination(i);

            pushPagination("...");
            pushPagination(last);

            // Add next button
            if (nextLink) links.push(nextLink);

            return links;
        },
        findPagination(label) {
            if (label === "...") {
                return paginationLinks.find((l) => l.label === "...") || { label: "...", url: null, active: false };
            }

            return paginationLinks.find((l) => Number(l.label) === label) || { label, url: null, active: false };
        },
    },

    watch: {
        records: {
            deep: true,
            handler(newRecords, oldRecords) {
                this.getTotalRecords();
                this.$nextTick(() => {
                    this.updateCell();
                    this.changeTableHeight();
                });
            },
        },

        columns: {
            deep: true,
            handler(newRecords, oldRecords) {
                this.$nextTick(() => {
                    this.updateCell();
                });
            },
        },
        sortColumnProp: function (n, o) {
            if (n !== o) {
                this.sortColumn = n;
            }
        },
        dirProp: function (n, o) {
            if (n !== o) {
                this.dir = n;
            }
        },

        displayBarEnable: function (n, o) {
            this.changeTableHeight();
        },

        compactView(newVal) {
            this.localCompactView = newVal; // Sync with prop changes
        },

        datatableUniqueKey() {
            // Update/reset headerChecked based on current tab data
            this.headerChecked = false;
            this.updateHeaderCheckboxIndeterminate();
        },
    },

    mounted() {
        this.$emit("mounted");
        this.$emit("done_loading", this.records);
        this.updateCell();
        if (!window.EventBus) {
            window.EventBus = new Vue();
        }
        window.EventBus.$on("displayBarEnable", (displaybar) => (this.displayBarEnable = displaybar));
        this.getColumnPreference(this.columns); // for sticky column
        this.isIndeterminate;

        this.$nextTick(() => {
            this.changeTableHeight();

            if (this.tableId) {
                $(`#${this.tableId}_table`).on("hide.bs.collapse", () => {
                    this.visible = false;
                });
                $(`#${this.tableId}_table`).on("show.bs.collapse", () => {
                    this.visible = true;
                });
            }
        });
    },

    beforeDestroy() {
        if (this.tableId) {
            $(`#${this.tableId}_table`).off("hide.bs.collapse show.bs.collapse");
        }
    },

    updated() {
        this.syncRowSelectionCheckboxes();
        this.$nextTick(() => {
            if (typeof $ !== "undefined" && typeof $.fn?.tooltip === "function") {
                $(this.$el).find('[data-toggle="tooltip"]').tooltip("dispose").tooltip();
            }
        });
    },

    methods: {
        // The row checkbox is a native input bound with `:checked`. A row can be deselected
        // without its own component re-rendering (the row's reactive deps don't fire), which
        // leaves the box showing the browser's last state. The datatable itself does re-render on
        // every selection change, so mirror record.checked onto the DOM inputs here.
        syncRowSelectionCheckboxes() {
            if (!this.$el || typeof this.$el.querySelectorAll !== "function") {
                return;
            }
            const prefix = this.formattedDatatableUniqueKey + "_select_row_";
            this.$el.querySelectorAll("input.select_row").forEach((input) => {
                if (!input.id || !input.id.startsWith(prefix)) {
                    return;
                }
                const key = input.id.slice(prefix.length);
                const record =
                    this.records[key] ||
                    Object.values(this.records).find((r) => String(r && r[this.primaryKey]) === key);
                const want = !!(record && record.checked);
                if (input.checked !== want) {
                    input.checked = want;
                }
            });
        },

        //Overview page
        isMergedRow(record) {
            return typeof this.mergedRow === "function" && this.mergedRow(record);
        },

        isRecordVisible(record) {
            return typeof this.recordVisible !== "function" || this.recordVisible(record);
        },

        getRowClass(record, index) {
            return typeof this.rowClass === "function" ? this.rowClass(record, index) : "";
        },

        updateHeaderCheckboxIndeterminate() {
            this.$nextTick(() => {
                const isIndeterminate =
                    this.headerCheckboxIndeterminate && this.selectedRecords.length > 0 && !this.headerChecked;

                $("#" + this.formattedDatatableUniqueKey + "_header_checkbox").prop("indeterminate", isIndeterminate);
                $("#check_all_record_additional").prop("indeterminate", isIndeterminate);
            });
        },

        getSelectColumnStyle() {
            if (!this.selectColumnWidth) {
                return {};
            }

            return {
                width: this.selectColumnWidth,
                minWidth: this.selectColumnWidth,
                maxWidth: this.selectColumnWidth,
            };
        },

        hasRowControlColumn() {
            return !!this.rowControlColumnWidth;
        },

        getRowControlColumnStyle() {
            if (!this.rowControlColumnWidth) {
                return {};
            }

            return {
                width: this.rowControlColumnWidth,
                minWidth: this.rowControlColumnWidth,
                maxWidth: this.rowControlColumnWidth,
            };
        },

        shouldRenderBodyColumn(record, column) {
            if (!this.isMergedRow(record)) {
                return true;
            }

            return column.id === this.mergedRowColumnId || this.mergedRowPreservedColumnIds.includes(column.id);
        },

        getBodyColumnColspan(record, column) {
            if (!this.isMergedRow(record) || column.id !== this.mergedRowColumnId) {
                return null;
            }

            const visibleColumnCount = Object.values(this.columns).filter(
                (item) => !item.disabled && item.visible
            ).length;
            const visiblePreservedColumnCount = Object.values(this.columns).filter(
                (item) => !item.disabled && item.visible && this.mergedRowPreservedColumnIds.includes(item.id)
            ).length;
            const actionColumnCount = this.displayActionColumn ? 1 : 0;
            const spacerColumnCount = 1;

            return visibleColumnCount - visiblePreservedColumnCount + actionColumnCount + spacerColumnCount;
        },

        saveRecordsSequence() {
            this.$refs.bo_confirmation
                .activate(
                    this.generalTranslations.update_records,
                    this.generalTranslations.update_sequence_message,
                    this.generalTranslations.confirm,
                    this.generalTranslations.cancel,
                    "success"
                )
                .then((confirm) => {
                    if (confirm && !this.sortColumn && !Object.values(this.search).length) {
                        this.continueUpdate();
                    }
                });
        },

        continueUpdate() {
            const payload = this.recordData.map((record, index) => ({
                id: record.id,
                sequence: index + 1,
            }));

            this.$emit(
                "update_sequence",
                payload,
                this.defaultUpdateSequenceSuccessCallback,
                this.defaultUpdateSequenceFailureCallback
            );
        },

        applyCurrentOrderAsSequence() {
            const message = (
                this.generalTranslations.apply_as_sequence_confirm_message ??
                "This will replace your current custom sequence with the order sorted by :column. This action cannot be undone."
            ).replace(":column", this.sortedColumnLabel);

            this.$refs.bo_confirmation
                .activate(
                    this.generalTranslations.apply_as_sequence,
                    message,
                    this.generalTranslations.confirm,
                    this.generalTranslations.cancel,
                    "success"
                )
                .then((confirm) => {
                    if (!confirm) {
                        return;
                    }

                    const newValue = this.recordData;
                    let table = this.$el.querySelector("table");
                    let rows = table.querySelectorAll("tbody > tr");

                    rows.forEach((row, index) => {
                        const id = row.id.split("_")[1];
                        const item = newValue.find((i) => i.id == id);

                        if (item && "sequence" in item) {
                            item.sequence = index + 1;
                        }
                    });

                    newValue.sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0));
                    this.emitGenericEvent("tableRow_sequence_change", newValue);

                    this.clearSort();
                });
        },

        clearSort() {
            this.sortColumn = null;
            this.$nextTick(() => {
                this.localSort();
            });
        },

        activeFilters() {
            return Object.keys(this.search).map((columnId) => {
                const column = this.columns[columnId];
                const rawValue = this.search[columnId];
                const value = Array.isArray(rawValue)
                    ? rawValue.join(" - ")
                    : column?.labels && column.labels[rawValue] !== undefined
                    ? column.labels[rawValue]
                    : rawValue;

                return {
                    columnId,
                    label: column ? column.label : columnId,
                    value,
                };
            });
        },

        clearFilter(columnId) {
            if (this.columns[columnId]) {
                this.columns[columnId].search = null;
            }
            this.$delete(this.search, columnId);
            this.updateLocalSearchSort();
        },

        clearFilters() {
            Object.values(this.columns).forEach((column) => {
                column.search = null;
            });
            this.search = {};
            this.updateLocalSearchSort();
        },

        clearAllFilters() {
            this.sortColumn = null;
            this.clearFilters();
        },

        updateCell() {
            // For Journey-Plan
            const bodyHasClass = document.body.classList.contains("journey-plan-representative-management");
            var stickyColumnNumber = 0;

            if (bodyHasClass) {
                const table = document.querySelector(".bo-table");

                // Set rowspan for the search header
                const firstRow = table.querySelector("tr");
                const secondRow = table.querySelector("thead tr:nth-child(2)");
                const stickyThElements = firstRow.querySelectorAll("th.stickyColumn");
                stickyColumnNumber = stickyThElements.length;

                firstRow.querySelector("th").setAttribute("rowspan", "2");
                firstRow.querySelector("th").style.zIndex = "11";

                for (const header of stickyThElements) {
                    header.setAttribute("rowspan", "2");
                }
                if (secondRow) {
                    const emptyCells = secondRow.querySelectorAll(`th:nth-child(-n+${stickyColumnNumber})`);

                    const secondRowHeader = secondRow.querySelectorAll("th");
                    secondRowHeader.forEach(function (header, index) {
                        header.style.top = "40px";
                    });

                    emptyCells.forEach((emptyCell) => {
                        emptyCell.style.display = "none";
                    });
                }

                // Set sticky and left position to stickyColumn class
                const stickyCells = table.querySelectorAll(".stickyColumn");
                const rows = table.querySelectorAll("tr");

                rows.forEach(function (row, index) {
                    if (index === 2) {
                        row.querySelector("th").style.zIndex = "11";
                    }
                    const stickyColumns = row.querySelectorAll('.stickyColumn:not([style*="display: none"])');
                    const stickyHeaderColumns = row.querySelectorAll('th.stickyColumn:not([style*="display: none"])');
                    let accumulatedWidth = 40;
                    stickyColumns.forEach(function (column, index) {
                        const columnStyle = window.getComputedStyle(column);
                        const columnWidth = parseFloat(columnStyle.width);
                        column.style.left = accumulatedWidth + "px";
                        accumulatedWidth += columnWidth;
                    });
                    stickyHeaderColumns.forEach(function (column, index) {
                        column.style.zIndex = "11";
                    });
                });
            }
        },

        changeTitle(title) {
            this.title = title;
        },

        changeColumns(columns) {
            this.columns = columns;
        },

        emitGenericEvent(eventName, ...args) {
            if (this.$listeners[eventName]) {
                this.$emit(eventName, ...args);
            } else {
                this.$emit("generic", eventName, ...args);
                this.$emit(`after_${eventName}`, ...args);
            }
        },

        // getColumnById(columnId) {
        //     if (Array.isArray(this.columns)) {
        //         return this.columns.filter((column) => column.id == columnId).shift();
        //     }
        //     return null;
        // },

        getData(
            payload = this.getRequestPayload(),
            success = this.defaultGetDataSuccessCallback,
            failure = this.defaultGetDataFailureCallback
        ) {
            if (!this.disableClearSelectionWhenLoadingData) {
                this.clearSelection();
            }
            this.$emit("get_data", payload, success, failure);
        },

        defaultGetDataSuccessCallback(response) {
            this.refreshTable(response.data);
        },

        defaultGetDataFailureCallback(error) {
            alert(this.generalTranslations.error_general ?? "Something went wrong, please try again.");
        },

        resolveURL(rawUrl, index) {
            return this.resolvePlaceholders(rawUrl, this.records[index]);
        },

        resolvePlaceholders(str, record) {
            const array = str.matchAll(/~~(\w*)~~/g);

            for (const group of array) {
                if (group[1] in record) {
                    str = str.replace(new RegExp("~~" + group[1] + "~~", "g"), record[group[1]]);
                }
            }

            return str;
        },

        // updatePopupPosition(event) {
        //     this.headerPopupPositionLeft = this.headerPopupPositionLeftDefault - event.target.scrollLeft;
        //     this.$forceUpdate();
        // },

        setAllHeaders: function () {
            if (true === this.allHeadersSelected) {
                Object.values(this.columns).forEach(function (header) {
                    header.visible = true;
                });
            } else {
                Object.values(this.columns).forEach(function (header) {
                    header.visible = false;
                });
            }
            // sticky columns
            this.saveColumnPreference(this.columnSequence);
        },

        resetHeadersToDefault: function () {
            this.columns = this.defaultColumns;
            this.allHeadersSelected = true;
            this.setAllHeaders();
        },

        promptExternalIfNeeded(prompt, eventName, triggerPrompt = null) {
            // The proper way to solve this would be with function props where the prop
            // would accept an object with event names as the keys and the callbacks as values.
            // But the recommended vue js practice would be to use events.
            if (triggerPrompt == null) {
                this.triggerPrompt = this.modified;
            }
            if (this.externalPrompt && this.$listeners[eventName]) {
                this.$emit(eventName, this.modified, prompt);
            } else {
                this.boPromptIfNeeded(prompt, this.modified, this.confirmationRef);
            }
        },

        changePage(url) {
            const prompt = new Prompt(
                () => {
                    this.page = Number.parseInt(url.split("page=")[1]);
                    this.getData();
                },
                null,
                this.change_page_string,
                this.change_page_message_string,
                this.proceed_string,
                "warning"
            );
            this.promptExternalIfNeeded(prompt, "table_filter_prompt");
        },

        goToPage: function () {
            //Refactor pageNumber to use computed
            if ("" != this.pageNumber) {
                const prompt = new Prompt(
                    () => {
                        this.page = this.parsePageNumber(this.pageNumber);
                        this.getData();
                    },
                    null,
                    this.change_page_string,
                    this.change_page_message_string,
                    this.proceed_string,
                    "warning"
                );
                this.promptExternalIfNeeded(prompt, "table_filter_prompt");
            }
        },

        parsePageNumber: function (pageNumber) {
            return Math.min(Math.max(Number.parseInt(this.pageNumber), 1), this.pagination.last_page);
        },

        // showLoading()/hideLoading() come from the http mixin (#111741: coordinated page veil)

        refreshTable: function (tableData) {
            this.resetColumnStates();
            // this.records = tableData.data["records"];
            // this.miscData = tableData.data["extra"];
            // this.pagination = tableData.meta;

            // this.columns.forEach((column) => {
            //     //Dont use search config with this config
            //     if (column.reset_after_search) {
            //         column.search = "";
            //     }
            // });

            // if (this.pagination) {
            //     this.$emit("updateProductTotal", this.pagination.total);
            // }

            // update checkbox
            // if (this.selectMultiple) {
            //     this.records.forEach((record) => {
            //         record.checked = false;
            //         var recordId = this.getKeyWithSelectedPrefixIfNeeded(record[this.primaryKey]);
            //         var exists = this.selectedRecords.some(function (field) {
            //             return field === recordId;
            //         });
            //         if (exists) {
            //             record.checked = true;
            //         }
            //     });
            // }

            // if (tableData.data["dataHeaders"]) {
            //     for (var id in tableData.data["dataHeaders"]) {
            //         var option = tableData.data["dataHeaders"][id];
            //         let column = this.columns.find((p) => p.id == id);
            //         if (column) {
            //             if ("select" == column.type || "status" == column.type) {
            //                 column.options = option;
            //                 if (!column.options.find((p) => p.id == column.search)) {
            //                     column.search = "";
            //                 }
            //             } else if (
            //                 ("number" == column.type || "currency" == column.type) &&
            //                 "undefined" == typeof column.meta.min
            //             ) {
            //                 column.showPopup = false;
            //                 column.meta.min = option.range[0];
            //                 column.meta.max = option.range[1];
            //                 if (!column.meta.value[0] && !column.meta.value[1]) {
            //                     column.meta.value = [option.range[0], option.range[1]];
            //                 }
            //                 column.meta.enableCross = true;
            //             } else if ("product" == column.type) {
            //                 column.showPopup = false;
            //             }
            //         }
            //     }
            // }
            // this.$forceUpdate();
            this.$emit("done_loading", tableData);
        },

        // Not used anywhere
        // updateSearchWithAddParameter(array) {
        //     var searchString = "";

        //     this.columns.forEach((column) => {
        //         searchString += column.id + "~~~" + column.search + "___";
        //     });
        //     array.forEach((object) => {
        //         searchString += object.key + "~~~" + object.value + "___";
        //     });

        //     this.search = searchString;

        //     this.getData();
        // },

        getRequestPayload() {
            // Merge search filters into payload
            let params = {
                ...{
                    page: this.page,
                    limit: this.limit,
                    column: this.column,
                    customFilter: this.customFilterValues,
                },
                ...this.search,
            };

            if (this.sortColumn) params[`sort[${this.sortColumn}]`] = this.dir;

            return {
                params: params,
                headers: this.headers,
            };
        },

        // Format: id=search
        updateSearch: function (column) {
            if (this.localSearchSort) {
                if (column.search) {
                    this.search[column.id] = column.search;
                } else {
                    delete this.search[column.id];
                }

                this.updateLocalSearchSort(column);
            } else {
                if (column.search) {
                    if ("number" == column.type || "currency" == column.type) {
                        this.search[`${column.id}_min`] = column.search[0];
                        this.search[`${column.id}_max`] = column.search[1];
                    } else {
                        this.search[column.id] = column.search;
                    }
                    this.page = 1;
                } else delete this.search[column.id];

                const prompt = new Prompt(
                    () => {
                        this.getData();
                    },
                    () => {
                        var search_array = this.search.split("___");
                        var searches = [];
                        search_array.forEach((search) => {
                            var s = search.split("~~~");
                            searches[s[0]] = s[1];
                        });
                        this.columns.forEach((column) => {
                            column.search = searches[column.id];
                        });
                    },
                    this.change_page_string,
                    this.change_page_message_string,
                    this.proceed_string,
                    "warning"
                );
                this.promptExternalIfNeeded(prompt, "table_filter_prompt");
            }

            // Emit event for bo-bulk-action-result-modal
            this.$emit("search", {
                column: column,
                search: column.search,
            });
        },

        // bo-select-with-search works with plain strings, so the option labels are shown
        // and mapped back to the option value (e.g. the integration slug) that the backend filters on.
        searchSelectItems(column) {
            const options = Array.isArray(column.options) ? column.options : Object.values(column.options ?? {});

            return options.map((value) => column.labels?.[value] ?? value);
        },

        searchSelectValue(column) {
            if (column.search == null || column.search === "") {
                return "";
            }

            return column.labels?.[column.search] ?? column.search;
        },

        onSearchSelectChange(column, label) {
            const options = Array.isArray(column.options) ? column.options : Object.values(column.options ?? {});
            const matched = options.find((value) => (column.labels?.[value] ?? value) === label);

            column.search = matched ?? label;
            this.updateSearch(column);
        },

        updateDateSearch(column) {
            if (this.localSearchSort) {
                if (column.search && column.search[0] && column.search[1]) {
                    this.search[column.id] = [column.search[0], column.search[1]];
                } else {
                    delete this.search[column.id];
                }
                this.updateLocalSearchSort(column);
            } else {
                if (column.search && (column.search[0] || column.search[1])) {
                    // End-date columns store the day after the displayed date, so shift the start bound to match.
                    // The end bound already gets +1 day from bo-date-range's includeEndDay.
                    this.search[`${column.id}_min`] =
                        column.is_end_date && column.search[0]
                            ? moment(column.search[0]).add(1, "d").toDate()
                            : column.search[0];
                    this.search[`${column.id}_max`] = column.search[1];
                    this.page = 1;
                } else {
                    delete this.search[column.id];
                    delete this.search[`${column.id}_min`];
                    delete this.search[`${column.id}_max`];
                }

                const prompt = new Prompt(
                    () => {
                        this.getData();
                    },
                    null,
                    this.change_page_string,
                    this.change_page_message_string,
                    this.proceed_string,
                    "warning"
                );
                this.promptExternalIfNeeded(prompt, "table_filter_prompt");
            }
        },

        // Force data table to send two parameters of the column id with min and max appended
        updateRangeSearch(column, inclusive = false) {
            if (column.search && (column.search[0] || column.search[1])) {
                this.search[`${column.id}${inclusive ? "_min" : "_start"}`] = column.search[0];
                this.search[`${column.id}${inclusive ? "_max" : "_end"}`] = column.search[1];
                this.page = 1;
            } else {
                delete this.search[column.id];
                delete this.search[`${column.id}_min`];
                delete this.search[`${column.id}_max`];
                delete this.search[`${column.id}_start`];
                delete this.search[`${column.id}_end`];
            }

            const prompt = new Prompt(
                () => {
                    this.getData();
                },
                null,
                this.change_page_string,
                this.change_page_message_string,
                this.proceed_string,
                "warning"
            );
            this.promptExternalIfNeeded(prompt, "table_filter_prompt");
        },

        // Datatable Sorting
        sort(column) {
            if (true == column.orderable) {
                if (this.localSearchSort) {
                    if (this.sortColumn != column.id) {
                        this.sortColumn = column.id;
                        this.dir = "desc";
                    } else {
                        this.dir = this.dir == "asc" ? "desc" : "asc";
                    }
                    this.updateLocalSearchSort(column);
                } else {
                    if (this.sortColumn != column.id) {
                        this.sortColumn = column.id;
                        this.dir = "desc";
                    } else {
                        this.dir = this.dir == "asc" ? "desc" : "asc";
                    }

                    this.page = 1;
                    this.getData();
                }

                // Emit event for bo-bulk-action-result-modal
                this.$emit("sort", {
                    column: this.sortColumn,
                    direction: this.dir,
                });
            }
        },

        updateLocalSearchSort(column) {
            let table = this.$el.querySelector("table");
            let rows = table.querySelectorAll("tbody > tr");

            for (const row of rows) {
                if (row.classList.contains("bo-empty-data")) {
                    row.style.display = "table-row";
                    continue;
                }
                if (Object.values(this.search).length > 0) {
                    let check = true;
                    this.localDraggable = false;
                    if (this.clearSelectionAfterSearch) {
                        this.clearSelection();
                    }
                    for (const [columnId, searchValue] of Object.entries(this.search)) {
                        const searchColumn = this.columns[columnId] ?? column;

                        if (searchValue instanceof Array) {
                            // Currently using isNaN to check whether the data provided is date or num
                            // Treat it as if it is not number, then it is date
                            if (isNaN(searchValue[0])) {
                                if (!this.localDateSearch(columnId, searchValue, row, searchColumn)) {
                                    check = false;
                                    break;
                                }
                            } else {
                                if (!this.localNumberRangeSearch(columnId, searchValue, row, searchColumn)) {
                                    check = false;
                                    break;
                                }
                            }
                        } else {
                            if (!this.localSearch(columnId, searchValue, row, searchColumn)) {
                                check = false;
                                break;
                            }
                        }
                    }
                    if (check) {
                        row.style.display = "table-row";
                    } else {
                        row.style.display = "none";
                    }
                } else {
                    this.localDraggable = true;
                    if (this.clearSelectionAfterSearch) {
                        this.clearSelection();
                    }
                    row.style.display = "table-row";
                }
            }

            let visibleRowsCount = 0;
            let dataRowsCount = 0;
            for (const row of rows) {
                if (row.classList.contains("bo-empty-data")) {
                    continue;
                }
                dataRowsCount++;
                if (row.style.display !== "none") {
                    visibleRowsCount++;
                }
            }
            this.noResultsFiltered = dataRowsCount > 0 && visibleRowsCount === 0;

            this.localSort();
        },

        localSearch(columnId, searchValue, row, column) {
            let content = this.getContentFromHTML(row, columnId);
            content = content.replace(/[_-]/g, " ");
            content = content.replace(/&nbsp;/g, " ");
            if (typeof searchValue !== "string") {
                searchValue = searchValue.toString();
            }
            searchValue = searchValue.replace(/[_-]/g, " ");

            if (!column || column.type == "text" || column.type == "custom") {
                return content.toLowerCase().includes(searchValue.toLowerCase());
            } else if (column.type == "number" || column.type == "currency" || column.type == "number2") {
                let normalizedContent = content.replace(/,/g, "");
                let normalizedSearch = searchValue.replace(/,/g, "");
                return normalizedContent.includes(normalizedSearch) || content.includes(searchValue);
            } else if (column.type == "select") {
                return content.toLowerCase() === searchValue.toLowerCase();
            } else if (column.type == "toggle") {
                function toBoolean(value) {
                    if (typeof value === "boolean") return value;

                    if (typeof value === "string") {
                        let v = value.trim().toLowerCase();

                        if (v === "") return false; // empty string → false

                        switch (v) {
                            case "true":
                            case "1":
                            case "yes":
                            case "on":
                            case "y":
                                return true;
                            case "false":
                            case "0":
                            case "no":
                            case "off":
                            case "n":
                                return false;
                        }
                    }

                    if (typeof value === "number") {
                        return value !== 0; // 0 = false, nonzero = true
                    }

                    return Boolean(value); // fallback (null, undefined, objects, etc.)
                }

                content = content ? JSON.parse(content) : "false";
                searchValue = JSON.parse(searchValue);
                return toBoolean(content) == toBoolean(searchValue);
            }
        },

        localDateSearch(columnId, searchValue, row, column) {
            let content = this.getContentFromHTML(row, columnId);
            let minDate = new Date(searchValue[0]);
            let maxDate = new Date(searchValue[1]);
            let searchDate = new Date(content);
            return searchDate >= minDate && searchDate <= maxDate;
        },

        localNumberRangeSearch(columnId, searchValue, row, column) {
            let content = this.getContentFromHTML(row, columnId);

            // In case there is comma in the number
            content = parseFloat(content.replace(/,/g, ""));

            let minNum = searchValue[0];
            let maxNum = searchValue[1];
            if (typeof maxNum == "undefined") {
                return content >= minNum;
            } else {
                return content >= minNum && content <= maxNum;
            }
        },

        localSort() {
            if (!this.sortColumn) {
                const hasSequenceColumn = Object.values(this.columns).some((column) => column.for_sequence);
                if (this.showSortFilterStatusBar && hasSequenceColumn) {
                    const table = this.$el.querySelector("table");
                    const tbody = table.querySelector("tbody");
                    this.recordData.forEach((record) => {
                        const row = tbody.querySelector(`#row_${record[this.primaryKey]}`);
                        if (row) tbody.appendChild(row);
                    });
                }
                return;
            }
            if (this.clearSelectionAfterSort) {
                this.clearSelection();
            }
            let table = this.$el.querySelector("table");
            let switching = true;
            let rows, i, a, b, shouldSort;

            while (switching) {
                switching = false;

                rows = table.querySelectorAll("tbody > tr");

                for (i = 0; i < rows.length; i++) {
                    if (rows[i + 1]) {
                        a = rows[i].querySelector("td." + (this.sortColumn + "").replace("_", "-"));
                        b = rows[i + 1].querySelector("td." + (this.sortColumn + "").replace("_", "-"));

                        let firstNum = a.querySelector("span")?.innerHTML.replace(",", "");
                        let secondNum = b.querySelector("span")?.innerHTML.replace(",", "");

                        let firstDate = Date.parse(firstNum?.replace(" ", ""));
                        let secondDate = Date.parse(secondNum?.replace(" ", ""));

                        if (!isNaN(firstNum) && !isNaN(secondNum)) {
                            if (
                                (this.dir == "asc" && parseFloat(firstNum) > parseFloat(secondNum)) ||
                                (this.dir == "desc" && parseFloat(firstNum) < parseFloat(secondNum))
                            ) {
                                shouldSort = true;
                                break;
                            }
                        } else if (!isNaN(firstDate) && !isNaN(secondDate)) {
                            if (
                                (this.dir == "asc" && firstDate > secondDate) ||
                                (this.dir == "desc" && firstDate < secondDate)
                            ) {
                                shouldSort = true;
                                break;
                            }
                        } else if (
                            (this.dir == "asc" && a.textContent.toLowerCase() > b.textContent.toLowerCase()) ||
                            (this.dir == "desc" && a.textContent.toLowerCase() < b.textContent.toLowerCase())
                        ) {
                            shouldSort = true;
                            break;
                        }
                    }
                }

                if (rows[i + 1] && shouldSort) {
                    rows[i].parentNode.insertBefore(rows[i + 1], rows[i]);
                    switching = true;
                }
            }
        },

        getContentFromHTML(row, columnId) {
            const cell = row.querySelector("td." + (columnId + "").replace("_", "-"));

            if (!cell) {
                return "";
            }

            const formField = cell.querySelector("input:not([type='checkbox']):not([type='radio']), textarea, select");
            if (formField) {
                if (formField.tagName === "SELECT") {
                    return formField.options[formField.selectedIndex]?.text ?? formField.value ?? "";
                }

                return formField.value ?? "";
            }

            let content = cell.textContent ?? "";

            let optionItem = cell.querySelector("div.option-item");
            if (optionItem) {
                content += " " + optionItem.textContent.trim();
            }

            return content.trim();
        },

        changePopupVisibility(event, column) {
            Object.keys(this.columns).forEach((key) => {
                this.columns[key].showPopup = false;
            });

            var elem_position = event.target.getBoundingClientRect();
            var bo_table_position = event.target.closest(".bo-table-div").getBoundingClientRect();

            if (event.target.closest(".modal-body")) {
                var modalOffset = event.target.closest(".modal-body").getBoundingClientRect();
                this.headerPopupPositionTop = window.pageYOffset + elem_position.top - modalOffset.top + 40;
                this.headerPopupPositionLeft = elem_position.left - modalOffset.left;
            } else {
                // main table
                this.headerPopupPositionTop = elem_position.top - bo_table_position.top + elem_position.height + 30;
                this.headerPopupPositionLeft = elem_position.left - bo_table_position.left + 30;
            }
            this.headerPopupPositionLeftDefault =
                this.headerPopupPositionLeft + this.$el.querySelector(".bo-table").scrollLeft;

            column.showPopup = true;
            this.$forceUpdate();
        },

        formattedTotal(total) {
            if (total) {
                return parseFloat(total)
                    .toFixed(0)
                    .replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1,");
            } else {
                return 0;
            }
        },

        changeTableVisibility() {
            this.visible = !this.visible;
        },

        changeCompactView() {
            this.compactView = !this.compactView;
        },

        toggleHeaderFiltersVisibility() {
            this.displayHeaderFilters = !this.displayHeaderFilters;
        },

        hideHeaderFilters(event) {
            this.displayHeaderFilters = false;
        },

        // Should be a slot
        // exportRecords(type) {
        //     var searchString = "";
        //     var columnNames = "";

        //     this.columns.forEach((column) => {
        //         searchString += column.id + "~~~" + column.search + "___";
        //         columnNames += column.id + "~~~" + column.label + "___";
        //     });

        //     var selectedRecords = [];
        //     if ("all" != type) {
        //         if (0 == this.selectedRecords.length) {
        //             alert("Kindly select at least one records before export.", "Export Records");
        //             return;
        //         }
        //         selectedRecords = this.selectedRecords;
        //     }

        //     if (this.beforeExport && this.beforeExport(type, this) === false) {
        //         return false;
        //     }

        //     this.showLoading();
        //     axios({
        //         url: this.exportUrl,
        //         method: "GET",
        //         responseType: "blob",
        //         params: {
        //             search: searchString,
        //             columnNames: columnNames,
        //             selectedRecords: selectedRecords,
        //             page: this.page,
        //             limit: this.limit,
        //         },
        //     })
        //         .then((response) => {
        //             var fileURL = window.URL.createObjectURL(new Blob([response.data]));
        //             var fileLink = document.createElement("a");

        //             fileLink.href = fileURL;
        //             fileLink.setAttribute("download", this.title + moment().format("_YYYY-MM-DD-HH-mm-ss") + ".xlsx");
        //             document.body.appendChild(fileLink);

        //             fileLink.click();
        //         })
        //         .catch((err) => {
        //             alert("Unable to export Data now.");
        //         })
        //         .then(() => {
        //             document.body.classList.remove("loading");
        //         });
        // },

        // EXCLUSIVE TO documents module only. vuex store is needed to use, called from all_document.js -> updateStatusInBulkEventHandler()
        // updateStatusInBulk() {
        //     this.showLoading();
        //     var url = document.getElementsByName("url")[0].getAttribute("content"); // for example: http://127.0.0.1:8000/test
        //     axios
        //         .post(url + "/documents/history/payment/updateStatus", {
        //             status: this.$store.state.status,
        //             document_ids: this.selectedRecords,
        //         })
        //         .then((response) => {
        //             this.headerChecked = false;
        //             this.selectedRecords = [];
        //             this.selectedRecordsCount = 0;
        //             this.getData();
        //             // loading screen will hide at refreshTable method
        //         })
        //         .catch(function (error) {
        //             console.log(error);
        //             alert("Unable to update status now.");
        //             document.body.classList.remove("loading");
        //         });
        // },

        modifyRecord(eventName, key, columnKey, record) {
            // this.modified = true;
            this.$emit("modify", key, columnKey, record);
            this.$emit(eventName, key, columnKey, record);
        },

        editRecordHandler(key, record, column) {
            if (column.for_sequence) {
                this.reassignSequenceFromManualEdit(key, record);
                return;
            }

            this.modifyRecord(`${column.type}_change_${column.id}`, key, column.id, record);
        },

        // Typing a new sequence value moves that row to the target position and shifts every
        // other row to make room, instead of just overwriting one value and leaving duplicates
        // (e.g. changing 5 to 2 in [1,2,3,4,5] should give [1,2(moved),3(was 2),4(was 3),5(was 4)]).
        reassignSequenceFromManualEdit(key, newValue) {
            const records = this.recordData;
            const targetSequence = parseInt(newValue, 10);

            if (isNaN(targetSequence)) {
                return;
            }

            const editedIndex = records.findIndex((record) => record[this.primaryKey] == key);
            if (editedIndex === -1) {
                return;
            }

            const clampedTarget = Math.max(1, Math.min(records.length, targetSequence));

            const [editedRecord] = records.splice(editedIndex, 1);
            records.splice(clampedTarget - 1, 0, editedRecord);

            records.forEach((record, index) => {
                if ("sequence" in record) {
                    record.sequence = index + 1;
                }
            });

            this.emitGenericEvent("tableRow_sequence_change", records);
        },

        editRecordFn(key, column) {
            return (event) => {
                this.editRecordHandler(key, event, column);
            };
        },

        //Recommended to intercept this event and change the callback function
        updatedSelectedRecords() {
            let label = this.selectedRecords.join("\n");
            const prompt = new Prompt(
                () => {
                    this.getData();
                    this.modified = false;
                },
                null,
                this.generalTranslations.update_records ?? "Update Records",
                (this.generalTranslations.update_records_question ??
                    "Are you sure you want to update the following records?") +
                    " " +
                    (this.generalTranslations.action_cannot_undone ?? "This action cannot be undone") +
                    "<br>" +
                    label,
                this.generalTranslations.update ?? "Update",
                "info"
            );
            this.promptExternalIfNeeded(prompt, "update_records_prompt", true);
        },

        editRecordInModal(recordKey) {
            this.$emit("show_edit_record_modal", recordKey);
        },

        saveRecordstoColumn(recordKey, column, records, id) {
            this.editRecordHandler(recordKey, records, column);
        },

        //Recommended to intercept this event and change the callback function
        deleteSelectedRecords() {
            let label = this.selectedRecords.join("\n");

            const prompt = new Prompt(
                () => {
                    this.emitGenericEvent("delete_multiple", this.selectedRecords, () => {
                        this.getData();
                        this.modified = false;
                    });
                },
                null,
                this.delete_records_string,
                this.delete_records_message_string + label,
                this.delete_string,
                "warning"
            );
            this.promptExternalIfNeeded(prompt, "delete_records_prompt", true);
        },

        //Recommended to intercept this event and change the callback function
        deleteRecord(recordKey) {
            const prompt = new Prompt(
                () => {
                    this.emitGenericEvent("delete", recordKey, () => {
                        this.getData();
                        this.modified = false;
                    });
                    this.emitGenericEvent("delete_multiple", [recordKey], () => {
                        this.getData();
                        this.modified = false;
                    }); // For those too lazy to handle two separate events
                },
                null,
                this.delete_records_string,
                this.delete_records_message_string + "<b>" + recordKey + "</b>",
                this.delete_string,
                "warning"
            );
            this.promptExternalIfNeeded(prompt, "delete_record_prompt", true);
        },

        goToUrl(id) {
            if (this.customDetailsPageUrl.trim() === "") {
                if (window.location.href.lastIndexOf("/") === window.location.href.length - 1) {
                    window.location.assign(`${window.location.href}${id}`);
                } else {
                    window.location.assign(`${window.location.href}/${id}`);
                }
            } else {
                window.location.assign(`${this.customDetailsPageUrl}/${id}`);
            }
        },

        goToLogUrl(id) {
            if (window.location.href.lastIndexOf("/") === window.location.href.length - 1) {
                window.location.assign(`${window.location.href}${id}/records`);
            } else {
                window.location.assign(`${window.location.href}/${id}/records`);
            }
        },

        orderRecords(records) {
            let ordered = [];

            for (const id in records) {
                if (records[id].order == null || typeof records[id].order == "string") {
                    ordered.push(records[id]);
                } else {
                    ordered[records[id].order] = records[id];
                }
            }

            ordered = ordered.filter((item) => item != null); // Remove holes in array

            if (this.draggable) {
                let currentSequence = Math.max(0, ...ordered.map((r) => r.sequence ?? 0));

                for (const record of ordered) {
                    if (record.sequence == null) {
                        record.sequence = ++currentSequence;
                    }
                }
            }

            return ordered;
        },

        checkAllRecords(event) {
            if (Object.values(this.search).length > 0) {
                let table = this.$el.querySelector("table");
                let rows = table.querySelectorAll("tbody > tr");
                if (event.target.checked) {
                    rows.forEach((row) => {
                        if (row.style.display === "table-row" || row.style.display === "") {
                            const recordKey = row.id.substring(row.id.indexOf("_") + 1);

                            const record =
                                this.records[recordKey] ||
                                Object.values(this.records).find((r) => r[this.primaryKey] == recordKey);
                            const isDisabled = record ? this.disableSelectedRow(record) : false;

                            if (!isDisabled && !this.selectedRecordKeys.includes(recordKey)) {
                                this.selectedRecordKeys.push(recordKey);
                                this.emitGenericEvent("select_multiple_records", recordKey, true);
                            }
                        }
                    });
                } else {
                    this.clearSelection();
                }
            } else {
                if (event.target.checked) {
                    this.selectAllRecords();
                } else {
                    this.clearSelection();
                }
            }
            this.updateHeaderCheckboxIndeterminate();
            this.$forceUpdate();
        },

        checkColumnRecords(event, column) {
            for (const [recordKey, record] of Object.entries(this.records)) {
                if (record.hasOwnProperty(column["id"])) {
                    record[column["id"]] = event.target.checked;
                }
            }
        },

        selectAllRecords() {
            this.selectedRecordKeys = [];

            for (const [recordKey, record] of Object.entries(this.records)) {
                const isDisabled = this.disableSelectedRow(record);
                if (isDisabled) continue;

                const index = this.selectedRecordKeys.indexOf(recordKey);
                if (index === -1) {
                    this.selectedRecordKeys.push(recordKey);
                } else {
                    this.selectedRecordKeys.splice(index, 1);
                }

                if (this.selectableRecords[recordKey] != null) {
                    this.emitGenericEvent("select_multiple_records", recordKey, true);
                    const key = this.getKeyWithSelectedPrefixIfNeeded(recordKey);
                }
            }

            this.emitGenericEvent("selected_multiple_records", this.selectedRecords.slice(), this.primaryKey);
            this.updateHeaderCheckboxIndeterminate();
        },

        bulkUpdateReselectData(selectedRecords) {
            this.selectedRecordKeys = [];
            selectedRecords.forEach((selectedRecord) => {
                const index = this.selectedRecordKeys.indexOf(selectedRecord);
                if (index === -1) {
                    this.selectedRecordKeys.push(selectedRecord);
                }
                if (this.selectableRecords[selectedRecord] != null) {
                    this.emitGenericEvent("select_multiple_records", selectedRecord, true);
                    const key = this.getKeyWithSelectedPrefixIfNeeded(selectedRecord);
                }
            });

            this.emitGenericEvent("selected_multiple_records", this.selectedRecords.slice(), this.primaryKey);
            this.updateHeaderCheckboxIndeterminate();
            this.$forceUpdate();
        },

        clearSelection() {
            this.selectedRecordKeys = [];
            for (const [recordKey, record] of Object.entries(this.records)) {
                this.emitGenericEvent("select_multiple_records", recordKey, false);
            }

            this.emitGenericEvent("unselected_multiple_records", this.selectedRecords.slice(), this.primaryKey);
            this.headerChecked = false;
            this.selectedAllRecords = false;
            this.triggerSelectAll();
            this.updateHeaderCheckboxIndeterminate();
        },

        beforeWindowUnload(e) {
            e.returnValue = "";
        },

        resetColumnSearchStateAfterSearch(column) {
            if (column.resetAfterSearch) {
                column.search = "";
            }
        },

        resetColumnStates() {
            for (let column of Object.values(this.columns)) {
                this.resetColumnSearchStateAfterSearch(column);
            }
        },

        disableSelectedRow(record) {
            // if bo_can_select is false, cannot select this row
            if (record.hasOwnProperty("bo_can_select") && record.bo_can_select === false) {
                return true;
            }

            if (this.disableRow) {
                return this.disableRow(record);
            }

            return false;
        },

        changeSubRecordVisibility(record) {
            record.subrecordVisibility = !record.subrecordVisibility;
        },

        getKeyWithSelectedPrefixIfNeeded(key) {
            if (this.selectablePrefix != null) {
                return this.selectablePrefix + key;
            }

            return key;
        },

        isSelected(recordKey) {
            // The row highlight shares its source with the checkbox (record.checked), so a row
            // that arrives pre-checked — e.g. a select popup reopening with an existing
            // selection — renders as selected, and the two can never disagree.
            const record =
                this.records[recordKey] ??
                Object.values(this.records).find(
                    (candidate) => String(candidate?.[this.primaryKey]) === String(recordKey)
                );

            return Boolean(record?.checked);
        },

        selectSingleHandler(event, recordKey) {
            if (event.target.classList.contains("dropdown-trigger")) {
                return;
            }
            this.emitGenericEvent("selected_single_record", recordKey);

            // inject method from bo-popup-modal
            this.triggerPopupModalAction();
        },

        selectMultipleHandler(event, recordKey) {
            if (this.stopPropagation) {
                // stopPropagation to stops the bubbling phase
                event.stopPropagation();
            }

            if (event.target.classList.contains("dropdown-trigger")) {
                return;
            }
            let blackListClasses = ["bo-select-column"];
            let blackLists = ["bo-context-menu-more", "context-item", "item-command", "bo-select-column"];
            let excludeBlackListsInputType = ["checkbox", "radio", "file"];
            let blackListsParentIds = ["toggle"];
            if (blackLists.includes(event.target.tagName) && !excludeBlackListsInputType.includes(event.target.type)) {
                return;
            }

            if (blackListClasses.includes(event.target.className)) {
                return;
            }

            if (blackListsParentIds.includes(event.target.parentElement.id)) {
                return;
            }

            if (this.disableSelectedRow(this.records[recordKey])) {
                return;
            }

            const index = this.selectedRecordKeys.indexOf(recordKey.toString());

            if (index === -1) {
                this.selectedRecordKeys.push(recordKey.toString());
            } else {
                this.selectedRecordKeys.splice(index, 1);
            }

            this.updateHeaderCheckboxIndeterminate();

            this.emitGenericEvent("select_multiple_records", recordKey);
            this.emitGenericEvent("selected_multiple_records", this.selectedRecords.slice());
        },

        subrecordChangeHandler(key, subrecordKey, subrecord, columnType) {
            this.records[key].subrecords[subrecordKey] = subrecord;
            this.modifyRecord(key);

            this.$emit(`subrecord_${columnType}_change`, key, subrecordKey, subrecord);
        },

        subrecordDeleteHandler(key, subrecordKey, subrecord) {
            const prompt = new Prompt(
                () => {
                    this.$emit("delete_subrecord", key, subrecordKey);
                    this.getData();
                    this.modified = false;
                },
                null,
                this.delete_records_string,
                this.delete_records_message_string + subrecordKey,
                this.delete_string,
                "warning"
            );
            this.promptExternalIfNeeded(prompt, "delete_record_prompt", true);
        },
        //workaround old template compiler
        getColumnMax(column, record) {
            return column.max ?? record[column.maxId] ?? null;
        },
        isRecordEditable(record, column) {
            if (!this.editable) {
                return false;
            }

            if (!column.editable) {
                return false;
            }

            if (column.recordEditable != null && typeof column.isRecordEditable == "function") {
                return column.isRecordEditable(record);
            }

            // Opt-in: while sorted/filtered, the visible row order doesn't represent the
            // manual sequence, so typing a new value directly would be confusing — same
            // reasoning as why dragging is blocked in that state (see dragHandleVisible).
            if (
                this.showSortFilterStatusBar &&
                column.for_sequence &&
                (this.sortColumn || Object.values(this.search).length)
            ) {
                return false;
            }

            if (this.recordEditable != null) {
                return this.recordEditable.call(this, record, column);
            }

            return true;
        },
        isRecordSelectable(record) {
            if (this.recordSelectable != null) {
                return this.recordSelectable.call(this, record);
            }

            return true;
        },
        printRecords(type) {
            if (0 == this.selectedRecords) {
                alert(
                    this.generalTranslations.print_records_message ?? "Kindly select at least one record before print.",
                    this.generalTranslations.print_records ?? "Print Records"
                );
                return;
            }

            this.showLoading();
            axios({
                url: this.printUrl,
                method: "GET",
                responseType: "blob",
                params: {
                    selectedRowIds: this.selectedRecords,
                    type: type,
                },
            })
                .then((response) => {
                    var fileURL = window.URL.createObjectURL(new Blob([response.data]));
                    var fileLink = document.createElement("a");

                    fileLink.href = fileURL;
                    fileLink.setAttribute("download", this.title + moment().format("_YYYY-MM-DD-HH-mm-ss") + ".pdf");
                    document.body.appendChild(fileLink);

                    fileLink.click();
                })
                .catch((err) => {
                    alert(this.generalTranslations.export_error ?? "Unable to export Data now.");
                })
                .then(() => {
                    this.hideLoading(); // #111741: coordinated page veil
                });
        },

        downloadRecords(url, file_name) {
            let payload = [];
            this.$emit("download_records", url, payload, file_name);
        },

        refreshRecords() {
            Object.entries(this.search).forEach(([key, value]) => {
                this.$set(this.columns[key], "search", null);
            });
            this.$set(this, "search", {});
            this.$set(this, "sortColumn", null);

            this.$emit("refresh_records");
        },

        triggerFileInput(menuId) {
            let variables = {
                ":title": this.title,
            };

            // the picker has to be opened by the click that confirms the prompt: the promise below
            // resolves only after the close transition, and browsers ignore a file dialog opened
            // outside the user gesture
            const openPicker = () => document.getElementById(menuId + "-file-import").click();

            this.$refs.bo_confirmation.activate(
                this.generalTranslations.upload_excel ?? "Upload Excel",
                this.replaceVariables(
                    (this.generalTranslations.upload_excel_message ??
                        "Are you sure want to upload Excel for <b>:title</b>?") + "<br>",
                    variables
                ),
                this.generalTranslations.confirm ?? "Confirm",
                "danger",
                null,
                true,
                true,
                null,
                false,
                openPicker
            );
        },

        importRecords(event, url) {
            this.$emit("upload_records", event, url);
            event.target.value = "";
        },

        getDetailsPageUrl(record, column) {
            if (column.relatedDocumentLink) {
                return record.admin_portal_document_id_link;
            }
            let value = record["id"];

            if (typeof column.customLink != "undefined") {
                value = record[column.customLink];
            }

            if (this.customRecordsPageUrl.trim() !== "") {
                return this.customRecordsPageUrl + "/" + value;
            } else if (this.customDetailsPageUrl.trim() === "") {
                return window.bo.overviewUrl + "/" + value;
            }
            return this.customDetailsPageUrl + "/" + value;
        },

        addHoverClass: function (e) {
            e.target.classList.add("hoveredTR");
        },

        removeHoverClass: function (e) {
            e.target.classList.remove("hoveredTR");
        },

        handleErrorMessageMouseEnter(e, tableId, columnId, record) {
            if (e.target.classList.contains("error-td")) {
                const { clientX, clientY } = e;

                this.errorContextMenuPosition.top = `${clientY - 125}px`;
                this.errorContextMenuPosition.left = `${clientX - 200}px`;
                this.errorContextMenu = true;

                if (record.errors && record.errors[columnId]) {
                    this.errorContextMenuMessage = record.errors[columnId];
                }
            }
        },

        handleErrorMessageMouseLeave() {
            this.errorContextMenu = false;
        },

        showContextMenu(event, row, tableId) {
            if (this.disableContextMenu) {
                return;
            }

            if (row?.show_action_menu === false) {
                return;
            }

            let tagNames = ["A", "IMG", "I", "H6"];
            let blackLists = ["bo-context-menu-more", "context-item", "item-command", "bo-select-column"];
            // Ignore for links, images and icons
            if (tagNames.includes(event.target.tagName)) {
                return;
            }
            if (blackLists.includes(event.target.className)) {
                return;
            }
            event.preventDefault();

            this.selectedRow = row;

            // The row's action button/dropdown are keyed by the table's primary key, which is
            // not necessarily `id` (e.g. the variation BOM table uses `material_id`).
            const recordKey = row[this.primaryKey];

            const uniqueTableRef = "datatable_" + tableId;

            const tableComponent = this.$refs[uniqueTableRef];

            if (tableComponent && tableComponent.$el) {
                let offsetX, offsetYRelativeToTable;
                let positionRelativeToOffsetParent = false;
                switch (this.calculateOffsetType) {
                    case "tradeAgreementManagement":
                    case "taskRuleManagement":
                        var actionBodyButtonOffset = $(
                            "#" + this.formattedDatatableUniqueKey + "_action_body_button_" + recordKey
                        ).offset();
                        offsetX = event.pageX + 2;
                        offsetYRelativeToTable = actionBodyButtonOffset.top;
                        break;
                    case "notificationOverview":
                    default:
                        // The menu is a bootstrap dropdown, so its transform resolves against its own
                        // offsetParent - not the table. The offsetParent is only measurable once the
                        // dropdown is visible, so the offsets are resolved in the callback below.
                        positionRelativeToOffsetParent = true;
                }

                $("#" + this.formattedDatatableUniqueKey + "_action_body_button_" + recordKey).click();
                setTimeout(
                    function () {
                        const menuElement = document.getElementById(
                            this.formattedDatatableUniqueKey + "_actions_column_div_" + recordKey
                        );

                        if (!menuElement) {
                            return;
                        }

                        if (positionRelativeToOffsetParent) {
                            // Place the menu relative to its own offsetParent (measurable only once it
                            // is visible): left edge just right of the cursor, vertically centred on it.
                            const offsetParentRect = (
                                menuElement.offsetParent ?? document.body
                            ).getBoundingClientRect();
                            offsetX = event.clientX - offsetParentRect.left + 2;
                            offsetYRelativeToTable =
                                event.clientY - offsetParentRect.top - menuElement.offsetHeight / 2;
                        }

                        $(menuElement).css(
                            "transform",
                            "translate3d(" + offsetX + "px, " + offsetYRelativeToTable + "px, 0px)"
                        );

                        // Popper re-applies its own transform whenever the page scrolls, which snaps
                        // the menu back onto the row's action button. This (right-click) open is
                        // positioned manually, so stop this instance's scroll/resize listeners. A
                        // fresh Popper is created on the next open, so regular opens are unaffected.
                        const actionButtonElement = document.getElementById(
                            this.formattedDatatableUniqueKey + "_action_body_button_" + recordKey
                        );
                        const dropdownInstance = actionButtonElement
                            ? $(actionButtonElement).data("bs.dropdown")
                            : null;
                        dropdownInstance?._popper?.disableEventListeners?.();

                        // A window resize reflows the page underneath the menu, which would leave it
                        // detached from (and possibly overflowing) its row. Dismiss it rather than
                        // re-deriving the cursor position - the same thing a native context menu does.
                        if (actionButtonElement) {
                            const closeOnResize = function () {
                                $(actionButtonElement).dropdown("hide");
                            };
                            $(window).on("resize", closeOnResize);
                            $(menuElement.parentElement).one("hidden.bs.dropdown", function () {
                                $(window).off("resize", closeOnResize);
                            });
                        }
                    }.bind(this),
                    0
                );
            }
        },

        closeContextMenu(event) {
            const isOutsideMenu = !event.target.closest(".custom-menu");

            if (isOutsideMenu) {
                this.showMenu = false;
                document.removeEventListener("click", this.closeContextMenu);
            }
        },

        getMenuWidthHeight() {
            // Get the actual menu height by measuring the height of the hidden context menu
            const contextMenuElement = this.$refs.contextMenu;
            const tempDisplay = contextMenuElement.style.display;
            contextMenuElement.style.display = "block";
            this.menuHeight = contextMenuElement.offsetHeight;
            this.menuWidth = contextMenuElement.offsetWidth;
            contextMenuElement.style.display = tempDisplay;
        },

        // Sticky column - save column preference to db
        saveColumnPreference(columnSequence) {
            if (!this.enableColumnPreference) {
                return;
            }
            if (!Boolean(this.columnPreferenceUrl) || !Boolean(this.formattedDatatableUniqueKey)) return;

            // from columnSequence, saving only the id and visible property
            const columnIdVisibilityData = columnSequence.map((column) => {
                return {
                    id: column.id,
                    visible: column.visible,
                };
            });
            const payload = {
                datatable_key: this.formattedDatatableUniqueKey,
                column_sequence: columnIdVisibilityData,
            };

            // If bo-search-number column at the table last column then should set to true
            if (columnIdVisibilityData[6].id == "point") {
                this.pointsAtLast = true;
            } else {
                this.pointsAtLast = false;
            }

            this.saveColumnPreferenceData(
                this.columnPreferenceUrl,
                payload,
                (response) => {
                    // console.log("Request succeeded:", response);
                },
                (error) => {
                    // console.log("Request failed:", error);
                }
            );
        },

        // Sticky column - get column preference from db
        getColumnPreference(existingColumns) {
            if (!Boolean(this.columnPreferenceUrl) || !Boolean(this.formattedDatatableUniqueKey)) return;

            const url = this.columnPreferenceUrl + "/" + 1; // 1 is dummy id, just to call the get route
            const payload = {
                datatable_key: this.formattedDatatableUniqueKey,
            };
            const params = new URLSearchParams(payload);
            const queryString = "?" + params.toString();

            this.getColumnPreferenceData(
                url + queryString,
                (response) => {
                    if (response.data && response.data.column_preference) {
                        const columnPreference = JSON.parse(response.data.column_preference);
                        this.$emit("datatable_default_column_preference", columnPreference);
                        this.setColumnPreference(columnPreference);
                    }
                },
                (error) => {
                    // console.log("Request failed:", error);
                }
            );
        },

        // onDragChange() {
        //     this.isPostingColumnPreferenceEnabled = true;
        // },

        showPopupMessage(record, column) {
            this.boDataTableConfirmationCancel = false;
            this.boDataTableConfirmationCustomModalBackdrop = false;

            let data = column.transformer == null ? record[column.id] : column.transformer.call(column, record);

            this.$refs.bo_confirmation.activate(
                column?.label ?? "Description",
                data,
                null,
                null,
                null,
                false,
                column?.v_html ?? true
            );
        },

        handleColumnMouseEnter(event, record, column, index) {
            if (
                this.previousTooltipId != null &&
                this.previousTooltipColumn != null &&
                this.recordData[this.previousTooltipId]["showTooltips_" + this.previousTooltipColumn]
            ) {
                return;
            }

            if (this.previousShowViewMoreButtonId != null && this.previousShowViewMoreButtonColumn != null) {
                this.$set(
                    this.recordData[this.previousShowViewMoreButtonId],
                    "showViewMoreButton_" + this.previousShowViewMoreButtonColumn,
                    false
                );
            }

            const element = event.currentTarget;
            const textSpan = element.querySelector("span");
            if (textSpan) {
                const isTruncated =
                    textSpan.scrollWidth > element.clientWidth ||
                    textSpan.scrollWidth > textSpan.clientWidth ||
                    element.scrollWidth > element.clientWidth ||
                    textSpan.scrollHeight > element.clientHeight ||
                    element.scrollHeight > element.clientHeight ||
                    (textSpan.offsetHeight > 0 &&
                        element.clientHeight > 0 &&
                        textSpan.offsetHeight > element.clientHeight);
                this.$set(this.recordData[index], "isTruncated_" + column.id, isTruncated);
            }

            this.$set(this.recordData[index], "showViewMoreButton_" + column.id, true);
            this.showViewMoreButton = true;
            this.previousShowViewMoreButtonId = index;
            this.previousShowViewMoreButtonColumn = column.id;
        },
        handleColumnMouseLeave(record, column, index) {
            if (
                !(
                    this.previousTooltipId != null &&
                    this.previousTooltipColumn != null &&
                    this.recordData[this.previousTooltipId]["showTooltips_" + this.previousTooltipColumn]
                ) &&
                this.previousShowViewMoreButtonId != null &&
                this.previousShowViewMoreButtonColumn != null
            ) {
                this.$set(
                    this.recordData[this.previousShowViewMoreButtonId],
                    "showViewMoreButton_" + this.previousShowViewMoreButtonColumn,
                    false
                );

                this.showViewMoreButton = false;
                this.previousShowViewMoreButtonId = null;
                this.previousShowViewMoreButtonColumn = null;
            }
        },
        handleViewMoreClick(record, column, index) {
            if (this.previousTooltipId != null && this.previousTooltipColumn != null) {
                if (this.previousTooltipId == index && this.previousTooltipColumn == column.id) {
                    this.closeTooltip(record, column, index);
                    return;
                } else {
                    this.recordData[this.previousTooltipId]["showTooltips_" + this.previousTooltipColumn] = false;
                }
            }
            this.recordData[index]["showTooltips_" + column.id] = true;
            this.previousTooltipId = index;
            this.previousTooltipColumn = column.id;
        },
        closeTooltip(record, column, index) {
            this.recordData[index]["showTooltips_" + column.id] = false;
            this.previousTooltipId = null;
            this.previousTooltipColumn = null;
        },

        triggerPopup(record, column) {
            this.records[record.id]["show_popup_modal"] = !this.records[record.id]["show_popup_modal"];
            this.getPopupModalColumns(column.subcolumns);

            this.fetchPopupModalRecords(column.get_records_route, null, null, null, record, column.payload_parameters);
        },

        fetchPopupModalRecords(url, payload = null, success = null, failure = null, record, payloadParameters) {
            if (payload == null) {
                var payload = {};
                payload.params = {};
            }

            Object.values(payloadParameters).forEach((parameter) => {
                payload.params[parameter["parameter_name"]] = record[parameter["record_variable"]];
            });

            this.getPopupModalRecords(url, payload, (records, pagination) => {
                this.popupModalRecords = {};
                this.popupModalPagination = pagination;

                records = records.map((record) => {
                    for (const key in this.columns) {
                        const column = this.columns[key];
                        if (column.type === "toggle") {
                            record[key] = !!record[key];
                        } else if (column.type === "datetime" || column.type === "date") {
                            if (record[key]) {
                                record[key] = new Date(moment.utc(record[key]).local());
                            }
                        }
                    }
                    return record;
                });

                this.boProcessRecords(this.popupModalRecords, records);
            });
        },

        getPopupModalRecords(url, payload, success, failure) {
            return httpMixin.methods.getData.call(
                this,
                url,
                payload,
                (response) => {
                    success(response.data.data, response.data.meta);
                },
                (error) => {}
            );
        },

        setColumnPreference(columnPreference) {
            // re-arranging the columns based on the column preference's sequence
            const temp = [];
            Object.values(columnPreference).forEach((dbColumn) => {
                const column = this.columnSequence.find((localColumn) => localColumn.id == dbColumn.id);
                if (column) {
                    // change the visibility of the column
                    column.visible = dbColumn.visible;
                    temp.push(column);
                }
            });

            // Handling extra columns in the this.columnSequence array
            if (temp.length < this.columnSequence.length) {
                const extraColumns = this.columnSequence.filter(
                    (localColumn) => !temp.find((column) => column.id == localColumn.id)
                );
                temp.push(...extraColumns);
            }

            //stop posting the column preference to db while loading the page
            // this.isPostingColumnPreferenceEnabled = false;
            this.$set(this, "columnSequence", temp);
        },

        getPopupModalColumns(column) {
            this.popupModalColumns = this.boDatatableProcessColumns(column);

            return this.popupModalColumns;
        },

        checkHasError(record, columnId) {
            if (record.errors && Object.keys(record.errors).includes(columnId)) {
                return true;
            }
            return false;
        },

        getErrorMessage(record, columnId) {
            if (record.errors && record.errors[columnId]) {
                return record.errors[columnId];
            }
            return null;
        },

        getClasses(record, columnId) {
            if (record.errors && Object.keys(record.errors).includes(columnId + "_extra")) {
                return "error-border";
            }
            return "";
        },

        conditionalCustomClasses(record, columnId) {
            let classes = "";

            if (
                record.custom_classes &&
                typeof record.custom_classes[columnId] != "undefined" &&
                typeof record.custom_classes[columnId]["current_value"] != "undefined" &&
                typeof record.custom_classes[columnId]["conditions"] != "undefined"
            ) {
                let currentValue = record.custom_classes[columnId]["current_value"];

                Object.values(record.custom_classes[columnId]["conditions"]).forEach(function (condition) {
                    // add custom class as defined in the simplified transformer if condition is true
                    if (
                        (condition["operator"] == ">" && currentValue > condition["value"]) ||
                        (condition["operator"] == ">=" && currentValue >= condition["value"]) ||
                        (condition["operator"] == "==" && currentValue == condition["value"]) ||
                        (condition["operator"] == "<=" && currentValue <= condition["value"]) ||
                        (condition["operator"] == "<" && currentValue < condition["value"])
                    ) {
                        if (classes == "") {
                            classes += condition["class"];
                        } else {
                            classes += " " + condition["class"];
                        }
                    }
                });
            }

            return classes;
        },

        selectAllRecordsValidation(value) {
            if (this.selectMultiple === true && this.headerChecked === true && value === false) {
                this.headerChecked = false;
                this.selectedAllRecords = false;
                this.triggerSelectAll();
                this.updateHeaderCheckboxIndeterminate();
            }
        },

        getTotalRecords() {
            if (this.pagination !== null && Object.values(this.pagination).length > 0) {
                if (typeof this.pagination.total != "undefined") {
                    this.totalAllRecords = this.pagination.total;
                }
            }
        },

        selectAll() {
            this.selectedAllRecords = true;
            this.triggerSelectAll();
        },

        triggerSelectAll() {
            this.$emit("selected_all_records", this.selectedAllRecords);
        },

        customFilter(column, value = null) {
            if (this.customFilters[column.id]) {
                this.$set(this.customFilters, column.id, !this.customFilters[column.id]);
            } else {
                this.$set(this.customFilters, column.id, value);
            }
        },

        isCustomFilterSelected(column, action) {
            let result = null;

            Object.values(this.customFilterValues).forEach((filter) => {
                if (filter.field == column.id && filter.action == action) {
                    if (result !== true) {
                        result = true;
                    }
                }
            });

            return result;
        },

        customFilterFunction(column, filter, reload = true) {
            let value = {};

            value.field = column.id;
            value.action = filter;

            this.$set(this.customFilterValues, column.id, value);

            if ((filter == "isEmpty" || filter == "isNotEmpty") && typeof this.search[column.id] != "undefined") {
                this.search[column.id] = null;
                this.columns[column.id].search = null;
            }

            if (reload) {
                this.updateSearch(column);
            }
        },

        handleCustomFilterClickedOutside(column, event) {
            const customFilterEl = document.getElementById(`${column.id}_header_custom_filter`);
            if (customFilterEl && customFilterEl.contains(event.target)) {
                // Click is inside the div, do nothing
                return;
            }

            this.customFilter(column, false);
        },

        showViewMore(column, record) {
            if (record["isTruncated_" + column.id] === true) {
                return true;
            }
            if (record["isTruncated_" + column.id] === false) {
                return false;
            }

            const content =
                typeof column.transform !== "undefined"
                    ? column.transform(record)
                    : this.formatCellContent(record, column);
            if (!content) {
                return false;
            }

            if (typeof content === "string" && content.length > 40) {
                return true;
            }

            // Shorter content is measured on hover (handleColumnMouseEnter); measuring here forces a layout per cell
            return false;
        },
        formatCellContent(record, column) {
            const rawContent = typeof column.transform !== "undefined" ? column.transform(record) : record[column.id];
            if (
                (column.id === "description" || column.id === "internal_description") &&
                typeof rawContent === "string"
            ) {
                return this.getFirstParagraph(rawContent);
            }
            return rawContent;
        },
        getFirstParagraph(content) {
            if (!content || typeof content !== "string") return content;

            // Extract all <p>...</p> tags
            const pMatches = content.match(/<p[^>]*>([\s\S]*?)<\/p>/gi);
            if (pMatches && pMatches.length > 0) {
                for (let i = 0; i < pMatches.length; i++) {
                    const plainText = pMatches[i]
                        .replace(/<[^>]+>/g, "")
                        .replace(/\s+/g, " ")
                        .trim();
                    if (plainText.length > 0) {
                        return plainText;
                    }
                }
            }

            // Fallback: strip tags, split by breaks, return first non-empty text line
            const cleanText = content.replace(/<br\s*\/?>/gi, "\n").replace(/<[^>]+>/g, "");
            const lines = cleanText.split(/\r?\n/);
            for (let i = 0; i < lines.length; i++) {
                const line = lines[i].trim().replace(/\s+/g, " ");
                if (line.length > 0) {
                    return line;
                }
            }

            return content;
        },
        showPagination(records, pagination) {
            let variables = {
                ":first": this.formattedTotal(pagination.from),
                ":last": this.formattedTotal(
                    (pagination.current_page - 1) * pagination.per_page + Object.keys(records).length
                ),
                ":total": this.formattedTotal(pagination.total),
            };
            return this.replaceVariables(
                this.generalTranslations.show_pagination ?? "Showing :first - :last of :total",
                variables
            );
        },
        editSingleOrMultiple(recordKey) {
            const selector = "#" + this.formattedDatatableUniqueKey + "_action_body_button_" + recordKey;
            $($.escapeSelector(selector)).click();

            const isSelectedMultiple = this.selectedRecords.length > 1;
            if (isSelectedMultiple) {
                // Emit show bulk update popup event
                this.$emit("edit_multiple", this.selectedRecords);
            } else {
                // Normal single edit operation
                this.goToUrl(recordKey);
            }
        },
        deleteSingleOrMultiple(recordKey) {
            const selector = "#" + this.formattedDatatableUniqueKey + "_action_body_button_" + recordKey;
            $($.escapeSelector(selector)).click();

            const isSelectedMultiple = this.selectedRecords.length > 1;
            if (isSelectedMultiple) {
                this.deleteSelectedRecords();
            } else {
                this.deleteRecord(recordKey);
            }
        },
    },
};

if (typeof $ !== "undefined" && typeof $.fn?.tooltip === "function") {
    $(function () {
        $('[data-toggle="tooltip"]').tooltip();
    });
}
</script>
